import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Chip,
  Button,
  InputAdornment,
  Pagination,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Dialog,
  DialogActions,
  AppBar,
  Toolbar,
  Drawer,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  IconButton,
  Avatar,
  Divider,
  FormControlLabel,
  Switch,
  useTheme,
  useMediaQuery,
  Rating,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Search,
  FilterList,
  ViewModule,
  ViewList,
  Security,
  Support,
  Map as MapIcon,
  VideoLibrary as VideoLibraryIcon,
  InfoOutlined as InfoOutlinedIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Diamond as DiamondIcon,
  Devices as DevicesIcon,
  Movie as MovieIcon,
  EmojiEvents as EmojiEventsIcon,
  SportsSoccer as SportsSoccerIcon,
  Home as HomeIcon,
  Man as ManIcon,
  Woman as WomanIcon,
  ChildCare as ChildCareIcon,
  MenuBook as MenuBookIcon,
  Close as CloseIcon,
  Menu as MenuIcon,
  Translate as TranslateIcon,
  AutoAwesome as AutoAwesomeIcon,
  AccountCircle as AccountCircleIcon,
  Work as WorkIcon,
  Message as MessageIcon,
  Logout as LogoutIcon,
  MyLocation as MyLocationIcon,
  Layers as LayersIcon,
  LocalShipping,
  Church,
  Mosque,
  Synagogue,
  NightsStay,
  SentimentDissatisfied as SentimentDissatisfiedIcon,
  ChevronLeft,
  ChevronRight,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation, useSearchParams, Routes, Route } from 'react-router-dom';
import ProductCard from '../components/Product/ProductCard';
import ProductReelsViewer from '../components/Product/ProductReelsViewer';
import FilterSidebar from '../components/FilterSidebar';
import SignInPromptModal from '../components/Auth/SignInPromptModal';
import ShareDialog from '../components/Product/ShareDialog';
import ProductDetailPage from './ProductDetailPage';
import { sortOptions, priceRanges, conditions } from '../data/mockProducts';
import { CategoryService } from '../services/categoryService';
import { Category } from '../lib/supabase';
import { UserService, UserSearchResult } from '../services/userService';
import { ProductService } from '../services/productService';
import { FavoritesService } from '../services/favoritesService';
import { useAuthStore } from '../store/authStore';
import { useCartStore } from '../store/cartStore';
import { useUIStore } from '../store/uiStore';
import { PlatformSettingsService, PlatformFeeSettings } from '../services/platformSettingsService';
import { calculatePriceWithFeesSync } from '../utils/priceUtils';
import { useTranslation } from '../contexts/TranslationContext';
import { translateText } from '../services/translationService';
import { defaultMegaMenuData } from '../components/Layout/MegaMenu';
import { useWebsiteSettings } from '../contexts/WebsiteSettingsContext';
import toast from 'react-hot-toast';
import { Product as CartProduct } from '../types';
import { supabase } from '../lib/supabase';
import { DeliveryOptionsService } from '../services/deliveryOptionsService';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, Marker, Popup, Tooltip as LeafletTooltip, useMap, useMapEvents } from 'react-leaflet';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const DEFAULT_MAP_CENTER: [number, number] = [40.7128, -74.0060];
const DEFAULT_MAP_ZOOM = 12;

const MapFlyTo: React.FC<{ center: [number, number]; zoom?: number }> = ({ center, zoom = DEFAULT_MAP_ZOOM }) => {
  const map = useMap();
  useEffect(() => {
    map.flyTo(center, zoom, { animate: true, duration: 0.9 });
  }, [center, zoom, map]);
  return null;
};

const MapCaptureInstance: React.FC<{ onMapReady: (map: L.Map) => void }> = ({ onMapReady }) => {
  const map = useMap();
  const didCallRef = React.useRef(false);
  useEffect(() => {
    if (didCallRef.current) return;
    didCallRef.current = true;
    onMapReady(map);
  }, [map, onMapReady]);
  return null;
};

const MapBoundsTracker: React.FC<{ onViewportChange: (bounds: L.LatLngBounds, size: L.Point) => void }> = ({ onViewportChange }) => {
  const map = useMap();
  const rafRef = React.useRef<number | null>(null);
  const emit = React.useCallback(() => {
    if (rafRef.current != null) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null;
      onViewportChange(map.getBounds(), map.getSize());
    });
  }, [map, onViewportChange]);

  useEffect(() => {
    onViewportChange(map.getBounds(), map.getSize());
  }, [map, onViewportChange]);

  useEffect(() => {
    return () => {
      if (rafRef.current != null) window.cancelAnimationFrame(rafRef.current);
    };
  }, []);

  useMapEvents({
    move: emit,
    zoom: emit,
    resize: emit,
    moveend: emit,
    zoomend: emit,
  });

  return null;
};

// Note: getProductsWithSellers returns a different product structure than the main Product type
interface ProductFromService {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  image: string;
  rating: number;
  reviewCount: number;
  category: string;
  sellerId: string;
  seller: {
    name: string;
    avatar: string;
    location: string;
  };
  isNew?: boolean;
  isFeatured?: boolean;
  discount?: number;
  description?: string;
  tags?: string[];
  inStock?: boolean;
  condition?: string;
  brand?: string;
  isJob?: boolean;
  // Job-specific fields
  company?: string;
  companyLogo?: string;
  location?: string;
  salary?: {
    min: number;
    max: number;
    currency: string;
  };
  employmentType?: string;
  experienceLevel?: string;
  isRemote?: boolean;
  postedDate?: string;
  requirements?: string[];
  benefits?: string[];
  duration?: string;
  applicantsCount?: number;
  viewsCount?: number;
  isUrgent?: boolean;
  requiredSkills?: string[];
  // Auction fields (optional in products listing)
  auctionId?: string;
  auction?: {
    id?: string;
    current_bid?: number;
  };
  // Private single buy flag
  is_private_singlebuy?: boolean;

  latitude?: number;
  longitude?: number;
  location_name?: string;
}

const AllProductsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t, language, autoTranslate, setLanguage, setAutoTranslate } = useTranslation();
  const { settings: siteSettings } = useWebsiteSettings();
  const tx = (key: string, fallback: string) => {
    const val = t(key as any) as unknown as string;
    return typeof val === 'string' && val !== key ? val : fallback;
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));

  const [nowTs, setNowTs] = React.useState(() => Date.now());

  React.useEffect(() => {
    const id = window.setInterval(() => setNowTs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const formatAuctionCountdown = React.useCallback((endTimeIso?: string | null): string => {
    const endIso = String(endTimeIso || '').trim();
    if (!endIso) return '';
    const end = new Date(endIso).getTime();
    if (!Number.isFinite(end)) return '';
    const diff = end - nowTs;
    if (diff <= 0) return 'Ended';
    const s = Math.floor(diff / 1000);
    const days = Math.floor(s / 86400);
    const hours = Math.floor((s % 86400) / 3600);
    const mins = Math.floor((s % 3600) / 60);
    const secs = s % 60;
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  }, [nowTs]);

  const mapMarkerIcon = React.useMemo(() => {
    const primary = theme.palette.primary.main;
    const dark = theme.palette.primary.dark;
    return L.divIcon({
      className: 'product-theme-marker',
      html: `
        <div style="
          width: 34px;
          height: 34px;
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          background: linear-gradient(135deg, ${primary} 0%, ${dark} 100%);
          border: 3px solid rgba(255,255,255,0.98);
          box-shadow: 0 10px 22px rgba(0,0,0,0.18), 0 0 0 6px rgba(255,255,255,0.14);
          position: relative;
        ">
          <div style="
            position: absolute;
            width: 14px;
            height: 14px;
            border-radius: 999px;
            background: rgba(255,255,255,0.95);
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%) rotate(45deg);
            box-shadow: 0 6px 14px rgba(0,0,0,0.18);
          "></div>
        </div>
      `,
      iconSize: [34, 34],
      iconAnchor: [17, 34],
      popupAnchor: [0, -34],
    });
  }, [theme.palette.primary.main, theme.palette.primary.dark]);

  const toCoord = React.useCallback((v: any): number | null => {
    const n = typeof v === 'number' ? v : (typeof v === 'string' ? Number(v) : NaN);
    return Number.isFinite(n) ? n : null;
  }, []);
  
  // Check if this is the jobs page
  const isJobsPage = location.pathname === '/all-jobs';

  const mapDrawerStorageKey = React.useMemo(() => {
    return `allProducts.mapDrawerOpen.${isJobsPage ? 'jobs' : 'products'}`;
  }, [isJobsPage]);

  const mapExpandedStorageKey = React.useMemo(() => {
    return `allProducts.mapExpanded.${isJobsPage ? 'jobs' : 'products'}`;
  }, [isJobsPage]);
  
  // Authentication state
  const { user, isAuthenticated, logout } = useAuthStore();
  const { addItem } = useCartStore();
  const {
    jobApplicationWriterOpen,
    jobApplicationWriterMinimized,
    setJobApplicationWriterOpen,
    setJobApplicationWriterMinimized,
  } = useUIStore();

  const [drawerLangAnchorEl, setDrawerLangAnchorEl] = useState<null | HTMLElement>(null);
  const [drawerUserAnchorEl, setDrawerUserAnchorEl] = useState<null | HTMLElement>(null);

  const supportedLanguages = ['en', 'de', 'tr'];
  const languageOptions = [
    { code: 'en', label: 'English', country: 'us', supported: true },
    { code: 'de', label: 'Deutsch', country: 'de', supported: true },
    { code: 'tr', label: 'Türkçe', country: 'tr', supported: true },
    { code: 'fr', label: 'Français', country: 'fr', supported: false },
    { code: 'es', label: 'Español', country: 'es', supported: false },
    { code: 'it', label: 'Italiano', country: 'it', supported: false },
    { code: 'pt', label: 'Português', country: 'pt', supported: false },
    { code: 'ar', label: 'العربية', country: 'sa', supported: false },
    { code: 'hi', label: 'हिन्दी', country: 'in', supported: false },
    { code: 'zh', label: '中文', country: 'cn', supported: false },
  ];

  const renderFlag = (opt: any, size: number, mr: number) => {
    const country = String(opt?.country || '').toLowerCase();

    const commonSx = {
      width: size,
      height: size,
      mr,
      borderRadius: 999,
      display: 'block',
      overflow: 'hidden',
      flexShrink: 0,
    } as const;

    const svgProps = {
      width: size,
      height: size,
      viewBox: '0 0 30 20',
      preserveAspectRatio: 'xMidYMid slice',
    } as any;

    switch (country) {
      case 'de':
        return (
          <Box sx={commonSx}>
            <svg {...svgProps} viewBox="0 0 3 2">
              <rect width="3" height="2" fill="#ffce00" />
              <rect width="3" height="1.333" y="0" fill="#dd0000" />
              <rect width="3" height="0.666" y="0" fill="#000" />
            </svg>
          </Box>
        );
      case 'tr':
        return (
          <Box sx={commonSx}>
            <svg {...svgProps} viewBox="0 0 3 2">
              <rect width="3" height="2" fill="#e30a17" />
              <circle cx="1.25" cy="1" r="0.55" fill="#fff" />
              <circle cx="1.38" cy="1" r="0.45" fill="#e30a17" />
              <polygon points="1.92,1 2.15,1.08 2.02,0.88 2.17,0.72 1.95,0.76 1.84,0.56 1.82,0.79 1.60,0.83 1.82,0.92" fill="#fff" />
            </svg>
          </Box>
        );
      case 'us':
        return (
          <Box sx={commonSx}>
            <svg {...svgProps}>
              <rect width="30" height="20" fill="#fff" />
              <g fill="#b22234">
                <rect width="30" height="2" y="0" />
                <rect width="30" height="2" y="4" />
                <rect width="30" height="2" y="8" />
                <rect width="30" height="2" y="12" />
                <rect width="30" height="2" y="16" />
              </g>
              <rect width="12" height="10" fill="#3c3b6e" />
              <g fill="#fff">
                <circle cx="2" cy="2" r="0.5" />
                <circle cx="4" cy="2" r="0.5" />
                <circle cx="6" cy="2" r="0.5" />
                <circle cx="8" cy="2" r="0.5" />
                <circle cx="10" cy="2" r="0.5" />
                <circle cx="3" cy="4" r="0.5" />
                <circle cx="5" cy="4" r="0.5" />
                <circle cx="7" cy="4" r="0.5" />
                <circle cx="9" cy="4" r="0.5" />
                <circle cx="2" cy="6" r="0.5" />
                <circle cx="4" cy="6" r="0.5" />
                <circle cx="6" cy="6" r="0.5" />
                <circle cx="8" cy="6" r="0.5" />
                <circle cx="10" cy="6" r="0.5" />
              </g>
            </svg>
          </Box>
        );
      case 'fr':
        return (
          <Box sx={commonSx}>
            <svg {...svgProps} viewBox="0 0 3 2">
              <rect width="1" height="2" x="0" fill="#0055a4" />
              <rect width="1" height="2" x="1" fill="#fff" />
              <rect width="1" height="2" x="2" fill="#ef4135" />
            </svg>
          </Box>
        );
      case 'es':
        return (
          <Box sx={commonSx}>
            <svg {...svgProps} viewBox="0 0 3 2">
              <rect width="3" height="2" fill="#c60b1e" />
              <rect width="3" height="1" y="0.5" fill="#ffc400" />
            </svg>
          </Box>
        );
      case 'it':
        return (
          <Box sx={commonSx}>
            <svg {...svgProps} viewBox="0 0 3 2">
              <rect width="1" height="2" x="0" fill="#009246" />
              <rect width="1" height="2" x="1" fill="#fff" />
              <rect width="1" height="2" x="2" fill="#ce2b37" />
            </svg>
          </Box>
        );
      case 'pt':
        return (
          <Box sx={commonSx}>
            <svg {...svgProps} viewBox="0 0 3 2">
              <rect width="1.2" height="2" x="0" fill="#006600" />
              <rect width="1.8" height="2" x="1.2" fill="#ff0000" />
              <circle cx="1.2" cy="1" r="0.35" fill="#ffd700" />
            </svg>
          </Box>
        );
      case 'sa':
        return (
          <Box sx={commonSx}>
            <svg {...svgProps} viewBox="0 0 3 2">
              <rect width="3" height="2" fill="#006c35" />
              <rect x="0.55" y="1.25" width="1.9" height="0.18" fill="#fff" />
            </svg>
          </Box>
        );
      case 'in':
        return (
          <Box sx={commonSx}>
            <svg {...svgProps} viewBox="0 0 3 2">
              <rect width="3" height="2" fill="#fff" />
              <rect width="3" height="0.666" y="0" fill="#ff9933" />
              <rect width="3" height="0.666" y="1.333" fill="#138808" />
              <circle cx="1.5" cy="1" r="0.18" fill="#000080" />
            </svg>
          </Box>
        );
      case 'cn':
        return (
          <Box sx={commonSx}>
            <svg {...svgProps} viewBox="0 0 3 2">
              <rect width="3" height="2" fill="#de2910" />
              <polygon points="0.5,0.3 0.6,0.55 0.85,0.55 0.65,0.7 0.73,0.95 0.5,0.8 0.27,0.95 0.35,0.7 0.15,0.55 0.4,0.55" fill="#ffde00" />
            </svg>
          </Box>
        );
      default:
        return (
          <Box sx={{ ...commonSx, backgroundColor: 'rgba(0,0,0,0.06)' }}>
            <Box sx={{ width: size, height: size }} />
          </Box>
        );
    }
  };

  const selectedLanguageOption = React.useMemo(() => {
    return languageOptions.find((o) => o.code === language) || languageOptions[0];
  }, [language]);

  const handleLanguageSelect = (code: string) => {
    if (supportedLanguages.includes(code)) {
      setLanguage(code as any);
    }
    setDrawerLangAnchorEl(null);
  };

  const [drawerUserEmail, setDrawerUserEmail] = useState<string | null>(null);
  React.useEffect(() => {
    const em = String((user as any)?.email || '').trim();
    setDrawerUserEmail(em || null);
  }, [isAuthenticated, (user as any)?.email]);

  const userMenuItems = React.useMemo(() => {
    const items: Array<{ label: string; path: string; icon: React.ReactNode }> = [
      { label: t('header.dashboard'), path: '/dashboard', icon: <AccountCircleIcon /> },
      { label: t('header.profile'), path: '/user-profile', icon: <AccountCircleIcon /> },
      ...(isJobsPage
        ? [{ label: t('header.interviews'), path: '/orders', icon: <WorkIcon /> }]
        : [{ label: t('header.orders'), path: '/orders', icon: <WorkIcon /> }]),
      ...((user as any)?.role === 'seller'
        ? [{ label: t('header.sellerDashboard'), path: '/seller', icon: <WorkIcon /> }]
        : []),
    ];
    return items;
  }, [isJobsPage, t, user]);
  
  // Cache for products data
  const productsCacheRef = React.useRef<Record<string, {
    products: ProductFromService[];
    total: number;
    timestamp: number;
  }>>({});
  
  // State management
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [sortBy, setSortBy] = useState('relevance');
  const [priceRange, setPriceRange] = useState<number[]>([0, 2000]);
  const [salaryType, setSalaryType] = useState<'any' | 'hourly' | 'fixed' | 'negotiable'>('any');
  const [selectedCondition, setSelectedCondition] = useState('all');
  const [showOnlyInStock, setShowOnlyInStock] = useState(false);
  const [showOnlyOnSale, setShowOnlyOnSale] = useState(false);
  const [excludeOwnProducts, setExcludeOwnProducts] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [reelsOpen, setReelsOpen] = useState(false);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [selectedBrands, setSelectedBrands] = useState<Set<string>>(new Set());
  const [minRating, setMinRating] = useState(0);
  const [requiredSkills, setRequiredSkills] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const [mapDrawerOpen, setMapDrawerOpen] = useState(() => {
    try {
      const stored = sessionStorage.getItem(mapDrawerStorageKey);
      if (stored === null) return false;
      return !!JSON.parse(stored);
    } catch {
      return false;
    }
  });

  const [mapExpanded, setMapExpanded] = useState(() => {
    try {
      const stored = sessionStorage.getItem(mapExpandedStorageKey);
      if (stored === null) return false;
      return !!JSON.parse(stored);
    } catch {
      return false;
    }
  });
  const [mapCenter, setMapCenter] = useState<[number, number]>(() => {
    try {
      const lat = toCoord(searchParams.get('lat'));
      const lng = toCoord(searchParams.get('lng'));
      if (lat == null || lng == null) return DEFAULT_MAP_CENTER;
      return [lat, lng];
    } catch {
      return DEFAULT_MAP_CENTER;
    }
  });
  const [mapZoom, setMapZoom] = useState<number>(() => {
    try {
      const zRaw = searchParams.get('zoom');
      const z = zRaw != null ? Math.round(Number(zRaw)) : NaN;
      if (!Number.isFinite(z)) return DEFAULT_MAP_ZOOM;
      return Math.max(1, Math.min(19, z));
    } catch {
      return DEFAULT_MAP_ZOOM;
    }
  });
  const lastAppliedPlaceRef = React.useRef<string>('');
  const inFlightPlaceRef = React.useRef<string>('');
  const pendingPlaceCenterRef = React.useRef<[number, number] | null>(null);
  const didAutoLocateRef = React.useRef(false);
  const leafletMapRef = React.useRef<L.Map | null>(null);
  const didInitLeafletMapRef = React.useRef(false);
  const didApplyDeepLinkViewRef = React.useRef(false);
  const mapInvalidateSizeTimeoutRef = React.useRef<number | null>(null);
  const didInitMapDrawerOpenRef = React.useRef(false);
  const loadedMapDrawerStorageKeyRef = React.useRef<string>(mapDrawerStorageKey);
  const didInitMapExpandedRef = React.useRef(false);
  const mapViewportRafRef = React.useRef<number | null>(null);
  const mapViewportCleanupRef = React.useRef<null | (() => void)>(null);
  const lastSyncedMapUrlRef = React.useRef<string>('');
  const latestPathnameRef = React.useRef<string>(location.pathname);
  const latestSearchRef = React.useRef<string>(location.search);
  const mapUrlSyncTimeoutRef = React.useRef<number | null>(null);

  const safeInvalidateSize = React.useCallback((delayMs = 0) => {
    if (mapInvalidateSizeTimeoutRef.current) {
      window.clearTimeout(mapInvalidateSizeTimeoutRef.current);
      mapInvalidateSizeTimeoutRef.current = null;
    }

    const run = () => {
      const map = leafletMapRef.current;
      if (!map) return;
      try {
        if (!(map as any)._loaded) return;
        map.invalidateSize();
      } catch {
        // ignore
      }
    };

    if (delayMs > 0) {
      mapInvalidateSizeTimeoutRef.current = window.setTimeout(run, delayMs);
      return;
    }

    run();
  }, []);

  useEffect(() => {
    latestPathnameRef.current = location.pathname;
    latestSearchRef.current = location.search;
  }, [location.pathname, location.search]);

  const deepLinkLocked = React.useMemo(() => {
    try {
      const lat = toCoord(searchParams.get('lat'));
      const lng = toCoord(searchParams.get('lng'));
      const zoomRaw = searchParams.get('zoom');
      const zoom = zoomRaw != null ? Number(zoomRaw) : NaN;
      return lat != null && lng != null && Number.isFinite(zoom);
    } catch {
      return false;
    }
  }, [searchParams, toCoord]);

  const handleMapReady = React.useCallback(
    (map: L.Map) => {
      if (didInitLeafletMapRef.current) return;
      didInitLeafletMapRef.current = true;
      leafletMapRef.current = map;
      map.whenReady(() => safeInvalidateSize(50));
      setMapSize(map.getSize());
      setMapReadyVersion((v) => v + 1);

      // If URL includes an explicit deep-link view, apply it once without animation.
      // After this, avoid any additional programmatic movements.
      try {
        if (deepLinkLocked && !didApplyDeepLinkViewRef.current) {
          const lat = toCoord(searchParams.get('lat'));
          const lng = toCoord(searchParams.get('lng'));
          const zoomRaw = searchParams.get('zoom');
          const zoom = zoomRaw != null ? Math.round(Number(zoomRaw)) : null;
          if (lat != null && lng != null && zoom != null && Number.isFinite(zoom)) {
            didApplyDeepLinkViewRef.current = true;
            setMapCenter([lat, lng]);
            setMapZoom(Math.max(1, Math.min(19, zoom)));
            map.setView([lat, lng], Math.max(1, Math.min(19, zoom)), { animate: false } as any);
          }
        }
      } catch {
        // ignore
      }

      // Keep viewport-driven UI in sync even if React-Leaflet event wiring is flaky.
      // (This also makes dev StrictMode / remounting more resilient.)
      try {
        const emit = () => {
          if (mapViewportRafRef.current != null) return;
          mapViewportRafRef.current = window.requestAnimationFrame(() => {
            mapViewportRafRef.current = null;
            try {
              setMapSize(map.getSize());
              setMapViewportVersion((v) => v + 1);
            } catch {
              // ignore
            }
          });
        };

        const syncUrl = () => {
          try {
            const c = map.getCenter();
            const z = Math.round(map.getZoom());
            setMapZoom(z);
            setMapCenter([c.lat, c.lng]);
          } catch {
            // ignore
          }
        };

        const scheduleUrlSync = () => {
          if (mapUrlSyncTimeoutRef.current != null) {
            window.clearTimeout(mapUrlSyncTimeoutRef.current);
            mapUrlSyncTimeoutRef.current = null;
          }
          mapUrlSyncTimeoutRef.current = window.setTimeout(() => {
            mapUrlSyncTimeoutRef.current = null;
            try {
              const c = map.getCenter();
              const z = Math.round(map.getZoom());
              const params = new URLSearchParams(latestSearchRef.current || '');
              params.set('lat', c.lat.toFixed(5));
              params.set('lng', c.lng.toFixed(5));
              params.set('zoom', String(z));
              const nextSearch = params.toString();
              const pathname = latestPathnameRef.current || location.pathname;
              const nextUrlKey = `${pathname}?${nextSearch}`;
              if (lastSyncedMapUrlRef.current === nextUrlKey) return;
              lastSyncedMapUrlRef.current = nextUrlKey;
              navigate({ pathname, search: nextSearch ? `?${nextSearch}` : '' }, { replace: true });
            } catch {
              // ignore
            }
          }, 220);
        };

        const events = ['move', 'zoom', 'resize', 'moveend', 'zoomend'] as const;
        events.forEach((ev) => map.on(ev, emit));
        map.on('moveend', scheduleUrlSync);
        map.on('zoomend', scheduleUrlSync);
        emit();
        syncUrl();
        scheduleUrlSync();

        mapViewportCleanupRef.current = () => {
          try {
            events.forEach((ev) => map.off(ev, emit));
            map.off('moveend', scheduleUrlSync);
            map.off('zoomend', scheduleUrlSync);
          } catch {
            // ignore
          }
          if (mapViewportRafRef.current != null) {
            window.cancelAnimationFrame(mapViewportRafRef.current);
            mapViewportRafRef.current = null;
          }
        };
      } catch {
        // ignore
      }

      // Apply any pending deep-linked center once the map is ready.
      const pending = pendingPlaceCenterRef.current;
      if (pending) {
        try {
          if (deepLinkLocked) return;
          map.flyTo(pending, mapZoom, { animate: true, duration: 0.9 } as any);
        } catch {
          // ignore
        }
      }
    },
    [deepLinkLocked, location.pathname, mapZoom, navigate, safeInvalidateSize, searchParams, toCoord]
  );

  // Deep-link support: /all-products?map=1&lat=..&lng=..&zoom=..
  useEffect(() => {
    const lat = toCoord(searchParams.get('lat'));
    const lng = toCoord(searchParams.get('lng'));
    const zoomRaw = searchParams.get('zoom');
    const zoom = zoomRaw != null ? Number(zoomRaw) : null;

    // If explicit coords are provided, force-open the map.
    if (lat !== null && lng !== null && Number.isFinite(zoom)) {
      setMapDrawerOpen(true);
    }

    if (lat === null || lng === null) return;
    if (!Number.isFinite(zoom)) return;

    // Authoritative deep-link: apply exactly once, and then never auto-adjust.
    if (!didApplyDeepLinkViewRef.current && Number.isFinite(zoom)) {
      didApplyDeepLinkViewRef.current = true;
    }

    // Avoid feedback-loop: when we update the URL after a drag/zoom, this effect will run.
    // If the URL matches the current map view, do nothing.
    try {
      const current = leafletMapRef.current?.getCenter();
      const currentZoom = leafletMapRef.current?.getZoom();
      const fmt = (v: number) => Number(v).toFixed(5);
      const sameCenter = !!current && fmt(current.lat) === fmt(lat) && fmt(current.lng) === fmt(lng);
      const zClamped = Number.isFinite(zoom) ? Math.max(1, Math.min(19, Number(zoom))) : null;
      const sameZoom = zClamped != null && currentZoom != null
        ? Math.round(currentZoom) === Math.round(zClamped)
        : false;
      if (sameCenter && sameZoom) {
        return;
      }
    } catch {
      // ignore
    }

    didAutoLocateRef.current = true;
    const next: [number, number] = [lat, lng];
    pendingPlaceCenterRef.current = next;
    setMapCenter(next);

    if (Number.isFinite(zoom)) {
      const z = Math.max(1, Math.min(19, Number(zoom)));
      setMapZoom(z);
    }

    // Apply deep-linked view once (no animation) and never adjust again automatically.
    try {
      const z = Number.isFinite(zoom) ? Math.max(1, Math.min(19, Math.round(Number(zoom)))) : mapZoom;
      const map = leafletMapRef.current;
      if (map) {
        map.setView(next, z, { animate: false } as any);
      }
    } catch {
      // ignore
    }
  }, [mapZoom, searchParams, toCoord]);

  useEffect(() => {
    return () => {
      if (mapInvalidateSizeTimeoutRef.current) {
        window.clearTimeout(mapInvalidateSizeTimeoutRef.current);
        mapInvalidateSizeTimeoutRef.current = null;
      }

      if (mapUrlSyncTimeoutRef.current != null) {
        window.clearTimeout(mapUrlSyncTimeoutRef.current);
        mapUrlSyncTimeoutRef.current = null;
      }

      if (mapViewportCleanupRef.current) {
        try {
          mapViewportCleanupRef.current();
        } catch {
          // ignore
        }
        mapViewportCleanupRef.current = null;
      }
    };
  }, []);
  const loadedMapExpandedStorageKeyRef = React.useRef<string>(mapExpandedStorageKey);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(mapDrawerStorageKey);
      if (stored === null) {
        loadedMapDrawerStorageKeyRef.current = mapDrawerStorageKey;
        return;
      }
      const parsed = !!JSON.parse(stored);
      loadedMapDrawerStorageKeyRef.current = mapDrawerStorageKey;
      setMapDrawerOpen(parsed);
    } catch {
      // ignore
      loadedMapDrawerStorageKeyRef.current = mapDrawerStorageKey;
    } finally {
      didInitMapDrawerOpenRef.current = true;
    }
  }, [mapDrawerStorageKey]);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(mapExpandedStorageKey);
      if (stored === null) {
        loadedMapExpandedStorageKeyRef.current = mapExpandedStorageKey;
        return;
      }
      const parsed = !!JSON.parse(stored);
      loadedMapExpandedStorageKeyRef.current = mapExpandedStorageKey;
      setMapExpanded(parsed);
    } catch {
      // ignore
      loadedMapExpandedStorageKeyRef.current = mapExpandedStorageKey;
    } finally {
      didInitMapExpandedRef.current = true;
    }
  }, [mapExpandedStorageKey]);

  useEffect(() => {
    if (!didInitMapDrawerOpenRef.current) return;
    if (loadedMapDrawerStorageKeyRef.current !== mapDrawerStorageKey) return;
    try {
      sessionStorage.setItem(mapDrawerStorageKey, JSON.stringify(mapDrawerOpen));
    } catch {
      // ignore
    }
  }, [mapDrawerOpen, mapDrawerStorageKey]);

  useEffect(() => {
    if (!didInitMapExpandedRef.current) return;
    if (loadedMapExpandedStorageKeyRef.current !== mapExpandedStorageKey) return;
    try {
      sessionStorage.setItem(mapExpandedStorageKey, JSON.stringify(mapExpanded));
    } catch {
      // ignore
    }
  }, [mapExpanded, mapExpandedStorageKey]);

  useEffect(() => {
    if (!mapDrawerOpen) return;
    const id = window.setTimeout(() => {
      safeInvalidateSize();
    }, 120);
    return () => window.clearTimeout(id);
  }, [mapDrawerOpen, safeInvalidateSize]);

  useEffect(() => {
    if (!mapDrawerOpen) return;
    const id = window.setTimeout(() => {
      safeInvalidateSize();
    }, 180);
    return () => window.clearTimeout(id);
  }, [mapExpanded, mapDrawerOpen, safeInvalidateSize]);
  
  // Server-side pagination state
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pageLoading, setPageLoading] = useState(false);
  
  // Custom filter fields
  const [customFilter1, setCustomFilter1] = useState('');
  const [customFilter2, setCustomFilter2] = useState('');
  const [customFilter3, setCustomFilter3] = useState('');
  const [customFilter4, setCustomFilter4] = useState('');
  const [customFilter5, setCustomFilter5] = useState('');
  // Product-only: auction vs direct-buy filter
  const [purchaseType, setPurchaseType] = useState<'any' | 'direct' | 'negotiable' | 'auction' | 'offer'>('any');
  
  React.useEffect(() => {
    if (isJobsPage) return;
    setAppliedFilters((prev) => {
      if (prev.purchaseType === purchaseType) return prev;
      return { ...prev, purchaseType };
    });
    setCurrentPage(1);
  }, [isJobsPage, purchaseType]);
  
  // Modal state
  const [signInModalOpen, setSignInModalOpen] = useState(false);

  const [jobInfoPanelOpen, setJobInfoPanelOpen] = useState(false);

  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [sharePayload, setSharePayload] = useState<{ id: string; url: string; title?: string } | null>(null);
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  
  // Auto-clear notification state
  const [showAutoClearNotification, setShowAutoClearNotification] = useState(false);
  
  // Favorites loading state
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  
  // Categories state
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);
  
  // Platform fee settings state
  const [platformFeeSettings, setPlatformFeeSettings] = useState<PlatformFeeSettings | null>(null);
  
  // Products state
  const [products, setProducts] = useState<ProductFromService[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productsError, setProductsError] = useState<string | null>(null);

  const [mapStyleAnchorEl, setMapStyleAnchorEl] = useState<null | HTMLElement>(null);
  const [mapStyleId, setMapStyleId] = useState<'osm' | 'hot' | 'carto-light' | 'carto-dark'>('osm');
  const [expandedMapCardId, setExpandedMapCardId] = useState<string | null>(null);
  const [mapSize, setMapSize] = useState<L.Point | null>(null);
  const [mapViewportVersion, setMapViewportVersion] = useState(0);
  const [mapReadyVersion, setMapReadyVersion] = useState(0);
  const [mapInfoVisible, setMapInfoVisible] = useState(true);
  const [mapPlaceSearchOpen, setMapPlaceSearchOpen] = useState(false);
  const [mapPlaceQuery, setMapPlaceQuery] = useState('');
  const [mapPlaceSearching, setMapPlaceSearching] = useState(false);
  const mapPlaceInputRef = React.useRef<HTMLInputElement | null>(null);
  const [mobileMapListAnchorEl, setMobileMapListAnchorEl] = useState<null | HTMLElement>(null);
  const mapClipRef = React.useRef<HTMLDivElement | null>(null);
  const [mapCornerRadiusPx, setMapCornerRadiusPx] = useState<number>(20);

  const mapViewInfo = React.useMemo(() => {
    const map = leafletMapRef.current;
    if (!map) return null;
    try {
      const center = map.getCenter();
      const zoom = Math.round(map.getZoom());
      return {
        lat: center.lat,
        lng: center.lng,
        zoom,
      };
    } catch {
      return null;
    }
  }, [mapViewportVersion, mapReadyVersion, mapDrawerOpen]);

  useEffect(() => {
    const el = mapClipRef.current;
    if (!el) return;
    const rStr = window.getComputedStyle(el).borderTopLeftRadius;
    const r = Number.parseFloat(rStr || '0');
    if (!Number.isFinite(r) || r <= 0) return;
    setMapCornerRadiusPx(r);
  }, [mapSize]);

  useEffect(() => {
    if (!mapPlaceSearchOpen) return;
    const id = window.setTimeout(() => {
      try {
        mapPlaceInputRef.current?.focus?.();
      } catch {
        // ignore
      }
    }, 0);
    return () => window.clearTimeout(id);
  }, [mapPlaceSearchOpen]);

  const productsWithCoords = React.useMemo(() => {
    return (products || [])
      .map((p) => {
        const lat = toCoord((p as any).latitude);
        const lng = toCoord((p as any).longitude);
        if (lat === null || lng === null) return null;
        return { p, lat, lng };
      })
      .filter(Boolean) as Array<{ p: ProductFromService; lat: number; lng: number }>;
  }, [products, toCoord]);

  const activeMarkerLatLng = React.useMemo(() => {
    if (expandedMapCardId) {
      if (expandedMapCardId === 'fallback') return L.latLng(mapCenter[0], mapCenter[1]);
      const hit = productsWithCoords.find((x) => String(x.p.id) === String(expandedMapCardId));
      if (!hit) return null;
      return L.latLng(hit.lat, hit.lng);
    }

    const first = productsWithCoords[0];
    if (first) return L.latLng(first.lat, first.lng);
    return L.latLng(mapCenter[0], mapCenter[1]);
  }, [expandedMapCardId, mapCenter, productsWithCoords]);

  const handleEdgeIndicatorClick = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const map = leafletMapRef.current;
    if (!map) return;

    const flyToWithLowerThirdAnchor = (targetLatLng: L.LatLng) => {
      const zoom = Math.max(map.getZoom(), 14);
      const size = map.getSize();
      const desiredY = size.y * 0.66;
      const deltaY = desiredY - size.y / 2;
      const targetPoint = map.project(targetLatLng, zoom);
      const centerPoint = targetPoint.subtract(L.point(0, deltaY));
      const centerLatLng = map.unproject(centerPoint, zoom);
      map.flyTo(centerLatLng, zoom, { animate: true, duration: 0.6 });
    };

    if (expandedMapCardId) {
      if (expandedMapCardId === 'fallback') {
        flyToWithLowerThirdAnchor(L.latLng(mapCenter[0], mapCenter[1]));
        setExpandedMapCardId('fallback');
        return;
      }

      const hit = productsWithCoords.find((x) => String(x.p.id) === String(expandedMapCardId));
      if (hit) {
        flyToWithLowerThirdAnchor(L.latLng(hit.lat, hit.lng));
        setExpandedMapCardId(String(hit.p.id));
      }
      return;
    }

    const first = productsWithCoords[0];
    if (first) {
      flyToWithLowerThirdAnchor(L.latLng(first.lat, first.lng));
      setExpandedMapCardId(String(first.p.id));
      return;
    }

    flyToWithLowerThirdAnchor(L.latLng(mapCenter[0], mapCenter[1]));
    setExpandedMapCardId('fallback');
  }, [expandedMapCardId, mapCenter, productsWithCoords]);

  useEffect(() => {
    if (!expandedMapCardId) return;

    let isDragging = false;
    const map = leafletMapRef.current;
    const mapContainer = map?.getContainer?.() as HTMLElement | undefined;

    const onDragStart = () => {
      isDragging = true;
    };
    const onDragEnd = () => {
      window.setTimeout(() => {
        isDragging = false;
      }, 0);
    };

    if (map) {
      map.on('dragstart', onDragStart);
      map.on('dragend', onDragEnd);
    }

    const onClick = (ev: MouseEvent) => {
      if (isDragging) return;
      const target = ev.target as HTMLElement | null;
      if (!target) return;
      if (target.closest('.product-map-tooltip')) return;
      if (target.closest('.map-edge-indicator-dot__label')) return;
      if (target.closest('.map-edge-indicator-dot-hit')) return;
      window.setTimeout(() => setExpandedMapCardId(null), 0);
    };

    mapContainer?.addEventListener('click', onClick, false);
    return () => {
      mapContainer?.removeEventListener('click', onClick, false);
      if (map) {
        map.off('dragstart', onDragStart);
        map.off('dragend', onDragEnd);
      }
    };
  }, [expandedMapCardId]);

  const edgeIndicatorPosition = React.useMemo(() => {
    if (!activeMarkerLatLng) return null;
    if (!mapSize) return null;
    const map = leafletMapRef.current;
    if (!map) return null;

    // Leaflet can throw during mount/unmount/resize while panes are not ready.
    // Guard against calling projection methods before the DOM is initialized.
    const container = (map as any).getContainer?.() as HTMLElement | undefined;
    const panes = (map as any).getPanes?.() as Record<string, HTMLElement> | undefined;
    const mapPane = panes?.mapPane;
    const isLoaded = Boolean((map as any)._loaded);
    if (!isLoaded || !container || !mapPane) return null;

    let p: L.Point;
    try {
      p = map.latLngToContainerPoint(activeMarkerLatLng);
    } catch {
      return null;
    }
    const dotOuterSize = 18;
    const r = dotOuterSize / 2;

    const isOutOfView = p.x < 0 || p.y < 0 || p.x > mapSize.x || p.y > mapSize.y;
    if (!isOutOfView) return null;

    const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
    const cornerRadius = Math.max(0, mapCornerRadiusPx);
    const cornerRadiusForCenter = Math.max(0, cornerRadius - r);

    const left = r + cornerRadiusForCenter;
    const right = mapSize.x - r - cornerRadiusForCenter;
    const top = r + cornerRadiusForCenter;
    const bottom = mapSize.y - r - cornerRadiusForCenter;

    const w = mapSize.x;
    const h = mapSize.y;
    const rr = Math.max(0, cornerRadiusForCenter);

    const segLeft = r;
    const segRight = w - r;
    const segTop = r;
    const segBottom = h - r;
    const innerLeft = segLeft + rr;
    const innerRight = segRight - rr;
    const innerTop = segTop + rr;
    const innerBottom = segBottom - rr;

    const qx = clamp(p.x, innerLeft, innerRight);
    const qy = clamp(p.y, innerTop, innerBottom);

    let vx = p.x - qx;
    let vy = p.y - qy;
    const vlen = Math.hypot(vx, vy) || 1;
    vx /= vlen;
    vy /= vlen;

    let x = qx + vx * rr;
    let y = qy + vy * rr;

    // Safety clamp (floating eps, extreme radii)
    x = clamp(x, segLeft, segRight);
    y = clamp(y, segTop, segBottom);

    return { x, y };
  }, [activeMarkerLatLng, mapCornerRadiusPx, mapReadyVersion, mapSize, mapViewportVersion]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    const map = leafletMapRef.current;
    if (!map || !mapSize || !activeMarkerLatLng) return;
    try {
      const container = (map as any).getContainer?.() as HTMLElement | undefined;
      const panes = (map as any).getPanes?.() as Record<string, HTMLElement> | undefined;
      const mapPane = panes?.mapPane;
      const isLoaded = Boolean((map as any)._loaded);
      if (!isLoaded || !container || !mapPane) return;

      const p = map.latLngToContainerPoint(activeMarkerLatLng);
      const isOutOfView = p.x < 0 || p.y < 0 || p.x > mapSize.x || p.y > mapSize.y;
      console.log('map top dot debug', {
        expandedMapCardId,
        edgeIndicatorPosition,
        activeMarkerLatLng: activeMarkerLatLng.toString(),
        containerPoint: { x: p.x, y: p.y },
        mapSize: { x: mapSize.x, y: mapSize.y },
        isOutOfView,
        mapReadyVersion,
        mapViewportVersion,
      });
    } catch {
      // ignore debug errors
    }
  }, [activeMarkerLatLng, edgeIndicatorPosition, expandedMapCardId, mapReadyVersion, mapSize, mapViewportVersion]);

  const handleMapViewportChange = React.useCallback((_bounds: L.LatLngBounds, size: L.Point) => {
    setMapSize(size);
    setMapViewportVersion((v) => v + 1);
  }, []);

  const mapTileProvider = React.useMemo(() => {
    const providers = {
      osm: {
        id: 'osm' as const,
        name: 'OpenStreetMap',
        url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        attribution: '© OpenStreetMap contributors',
      },
      hot: {
        id: 'hot' as const,
        name: 'OSM Humanitarian',
        url: 'https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png',
        attribution: '© OpenStreetMap contributors, Tiles style by HOT',
      },
      'carto-light': {
        id: 'carto-light' as const,
        name: 'Carto Light',
        url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
        attribution: '© OpenStreetMap contributors © CARTO',
      },
      'carto-dark': {
        id: 'carto-dark' as const,
        name: 'Carto Dark',
        url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
        attribution: '© OpenStreetMap contributors © CARTO',
      },
    };
    return providers[mapStyleId] || providers.osm;
  }, [mapStyleId]);
  
  // Seller search state
  const [selectedSeller, setSelectedSeller] = useState<UserSearchResult | null>(null);
  const [sellerSearchQuery, setSellerSearchQuery] = useState('');
  const [sellerSearchResults, setSellerSearchResults] = useState<UserSearchResult[]>([]);
  const [loadingSellerSearch, setLoadingSellerSearch] = useState(false);
  const [sellerSearchError, setSellerSearchError] = useState<string | null>(null);
  const [translatedNames, setTranslatedNames] = useState<Record<string, string>>({});
  const [translatedCategoryNames, setTranslatedCategoryNames] = useState<Record<string, string>>({});
  
  // Applied filters state (what's actually being used for filtering)
  const [appliedFilters, setAppliedFilters] = useState({
    searchQuery: '',
    selectedCategory: 'All Categories',
    sortBy: 'relevance',
    priceRange: [0, 2000] as number[],
    salaryType: 'any' as 'any' | 'hourly' | 'fixed' | 'negotiable',
    selectedCondition: 'all',
    showOnlyInStock: false,
    showOnlyOnSale: false,
    excludeOwnProducts: false,
    megaCategories: [] as string[],
    selectedBrands: new Set<string>(),
    minRating: 0,
    selectedSeller: null as UserSearchResult | null,
    requiredSkills: [] as string[],
    selectedTags: [] as string[],
    customFilter1: '',
    customFilter2: '',
    customFilter3: '',
    customFilter4: '',
    customFilter5: '',
    purchaseType: 'any' as 'any' | 'direct' | 'negotiable' | 'auction' | 'offer',
  });

  // Pretty label parsed from URL (e.g., "Herren / Schuhe") to ensure a visible chip
  const [urlCategoryChip, setUrlCategoryChip] = useState<string | null>(null);

  // Derive a fallback pretty label directly from URL (used during first render before effects run)
  const fallbackUrlChip = React.useMemo(() => {
    const c = searchParams.get('category');
    const s = searchParams.get('sub');
    if (c && s) return `${decodeURIComponent(c)} / ${decodeURIComponent(s)}`;
    return null;
  }, [searchParams]);

  const enabledTopCategories = React.useMemo(() => {
    const enabledMap = (siteSettings as any)?.categoriesEnabled as Record<string, boolean> | undefined;
    return Object.keys(defaultMegaMenuData)
      .filter((k) => k.toLowerCase() !== 'default')
      .filter((k) => {
        if (!enabledMap) return true;
        if (Object.prototype.hasOwnProperty.call(enabledMap, k)) {
          return !!enabledMap[k];
        }
        return true;
      });
  }, [siteSettings]);

  // Robustly decode possibly double-encoded strings
  const safeDecode = React.useCallback((value: string): string => {
    let prev = (value || '').replace(/\+/g, ' ');
    for (let i = 0; i < 3; i++) {
      try {
        const next = decodeURIComponent(prev);
        if (next === prev) return next;
        prev = next;
      } catch {
        return prev;
      }
    }
    return prev;
  }, []);

  // Deep-link support: /all-products?map=1&place=...
  useEffect(() => {
    const wantsMap = searchParams.get('map');
    const placeRaw = searchParams.get('place');
    const place = placeRaw ? safeDecode(placeRaw) : '';
    const placeNorm = place.trim();

    // If explicit coordinates are present in the URL, they take precedence over `place`.
    // This prevents a deep-link like ?lat=..&lng=..&place=.. from jumping after load.
    const latParam = toCoord(searchParams.get('lat'));
    const lngParam = toCoord(searchParams.get('lng'));

    // Always open map if requested via URL.
    if (wantsMap === '1') {
      setMapDrawerOpen(true);
    }

    if (wantsMap === '1' && latParam != null && lngParam != null) {
      setMapPlaceSearching(false);
      return;
    }

    // No place => nothing to geocode.
    if (wantsMap !== '1' || !placeNorm) {
      setMapPlaceSearching(false);
      return;
    }

    setMapPlaceSearching(true);

    // Prevent geolocation auto-centering from overriding a deep-link.
    didAutoLocateRef.current = true;

    // StrictMode-safe guard: React may mount/unmount effects twice in dev.
    // If the first run is aborted during cleanup, we must allow the second run.
    if (inFlightPlaceRef.current === placeNorm) return;
    inFlightPlaceRef.current = placeNorm;

    const ctrl = new AbortController();
    const run = async () => {
      try {
        const acceptLang = (() => {
          try {
            return (navigator.languages && navigator.languages.length > 0)
              ? navigator.languages[0]
              : (navigator.language || 'en');
          } catch {
            return 'en';
          }
        })();

        const geocodeNominatim = async (q: string) => {
          const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=3&addressdetails=1&accept-language=${encodeURIComponent(acceptLang)}`;
          const res = await fetch(url, { signal: ctrl.signal, headers: { Accept: 'application/json' } });
          if (!res.ok) return [] as Array<{ lat?: string; lon?: string; display_name?: string }>;
          const data = (await res.json()) as Array<{ lat?: string; lon?: string; display_name?: string }>;
          return Array.isArray(data) ? data : [];
        };

        const geocodeOpenMeteo = async (q: string) => {
          const url = `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(q)}&count=3&language=${encodeURIComponent(acceptLang)}&format=json`;
          const res = await fetch(url, { signal: ctrl.signal, headers: { Accept: 'application/json' } });
          if (!res.ok) return [] as Array<{ lat?: string; lon?: string; display_name?: string }>;
          const data = (await res.json()) as any;
          const results = Array.isArray(data?.results) ? data.results : [];
          return results.map((r: any) => ({
            lat: r?.latitude != null ? String(r.latitude) : undefined,
            lon: r?.longitude != null ? String(r.longitude) : undefined,
            display_name: [r?.name, r?.admin1, r?.country].filter(Boolean).join(', '),
            country_code: r?.country_code != null ? String(r.country_code) : undefined,
          }));
        };

        const fallbacks: string[] = [];
        const lower = placeNorm.toLowerCase();
        if (lower === 'colonia') {
          fallbacks.push('Köln');
          fallbacks.push('Cologne');
        }
        fallbacks.push(`${placeNorm} Germany`);
        fallbacks.push(`${placeNorm} Deutschland`);

        const queries = [placeNorm, ...fallbacks].filter(Boolean);
        let hits: Array<{ lat?: string; lon?: string; display_name?: string; country_code?: string }> = [];
        for (const q of queries) {
          // 1) Try Nominatim (may fail due to rate limits / CORS policies)
          try {
            hits = await geocodeNominatim(q);
          } catch {
            hits = [];
          }

          // 2) Fallback to Open-Meteo geocoding (CORS-friendly)
          if (!hits || hits.length === 0) {
            try {
              hits = await geocodeOpenMeteo(q);
            } catch {
              hits = [];
            }
          }

          if (process.env.NODE_ENV !== 'production') {
            console.log('[AllProductsPage][place geocode]', { q, hits: hits?.length || 0, first: hits?.[0]?.display_name });
          }
          if (hits.length > 0) break;
        }

        const bestHit = (() => {
          if (!hits || hits.length === 0) return undefined;
          const placeLower = placeNorm.toLowerCase();

          const score = (h: { display_name?: string; country_code?: string }) => {
            const dn = (h.display_name || '').toLowerCase();
            const cc = (h.country_code || '').toLowerCase();
            let s = 0;
            if (cc === 'de') s += 50;
            if (dn.includes('germany') || dn.includes('deutschland')) s += 40;
            if (dn.includes('köln') || dn.includes('cologne')) s += 60;
            if (placeLower === 'colonia' && (dn.includes('köln') || dn.includes('cologne'))) s += 100;
            // Minor preference for exact-ish name containment
            if (dn.includes(placeLower)) s += 5;
            return s;
          };

          return hits
            .map((h) => ({ h, s: score(h) }))
            .sort((a, b) => b.s - a.s)[0]?.h;
        })();

        const placeLower = placeNorm.toLowerCase();
        const hit = bestHit ?? hits[0];

        // Deterministic fallback for a common ambiguous term.
        // "colonia" is frequently used to mean Cologne/Köln (DE) but geocoders may return other places.
        const shouldForceCologne = (() => {
          if (placeLower !== 'colonia') return false;
          const dn = (hit?.display_name || '').toLowerCase();
          const cc = ((hit as any)?.country_code || '').toLowerCase();
          const looksLikeCologne = dn.includes('köln') || dn.includes('cologne');
          const looksLikeGermany = cc === 'de' || dn.includes('germany') || dn.includes('deutschland');
          return !(looksLikeCologne || looksLikeGermany);
        })();

        const lat = shouldForceCologne ? 50.9375 : (hit?.lat != null ? Number(hit.lat) : NaN);
        const lon = shouldForceCologne ? 6.9603 : (hit?.lon != null ? Number(hit.lon) : NaN);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
        if (process.env.NODE_ENV !== 'production') {
          console.log('[AllProductsPage][place chosen]', { place: placeNorm, lat, lon });
        }
        const next: [number, number] = [lat, lon];
        pendingPlaceCenterRef.current = next;
        setMapCenter(next);
        // Mark as applied only after we have a valid center.
        lastAppliedPlaceRef.current = placeNorm;
        // If the map is already ready, apply immediately.
        try {
          leafletMapRef.current?.flyTo(next, 12, { animate: true, duration: 0.9 } as any);
        } catch {
          // ignore
        }
      } catch {
        // ignore
      } finally {
        setMapPlaceSearching(false);
        if (inFlightPlaceRef.current === placeNorm) {
          inFlightPlaceRef.current = '';
        }
      }
    };

    run();
    return () => {
      ctrl.abort();
      setMapPlaceSearching(false);
      if (inFlightPlaceRef.current === placeNorm) {
        inFlightPlaceRef.current = '';
      }
    };
  }, [safeDecode, searchParams, toCoord]);

  // If the map drawer opens later (or map finishes initializing later), ensure we apply pending center.
  useEffect(() => {
    if (!mapDrawerOpen) return;
    if (deepLinkLocked) return;
    const pending = pendingPlaceCenterRef.current;
    if (!pending) return;
    const map = leafletMapRef.current;
    if (!map) return;
    try {
      // Avoid overriding an already-correct deep-linked view.
      const fmt = (v: number) => Number(v).toFixed(5);
      const c = map.getCenter();
      const z = Math.round(map.getZoom());
      const sameCenter = fmt(c.lat) === fmt(pending[0]) && fmt(c.lng) === fmt(pending[1]);
      const sameZoom = z === Math.round(mapZoom);
      if (sameCenter && sameZoom) return;

      map.flyTo(pending, Math.round(mapZoom), { animate: true, duration: 0.6 } as any);
    } catch {
      // ignore
    }
  }, [deepLinkLocked, mapDrawerOpen, mapReadyVersion, mapZoom]);

  // Default behavior: when opening the map (without a deep-link place), try geolocation.
  // If geolocation fails/denied, keep the default center (New York).
  useEffect(() => {
    if (!mapDrawerOpen) return;
    if (didAutoLocateRef.current) return;

    // Don't override deep-links.
    const wantsMap = searchParams.get('map');
    const placeRaw = searchParams.get('place');
    const place = placeRaw ? safeDecode(placeRaw) : '';
    if (wantsMap === '1' && place.trim()) return;
    if (deepLinkLocked) return;
    if (pendingPlaceCenterRef.current) return;

    didAutoLocateRef.current = true;
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const next: [number, number] = [pos.coords.latitude, pos.coords.longitude];
        pendingPlaceCenterRef.current = next;
        setMapCenter(next);
        try {
          leafletMapRef.current?.flyTo(next, 12, { animate: true, duration: 0.9 } as any);
        } catch {
          // ignore
        }
      },
      () => {
        // keep DEFAULT_MAP_CENTER (New York)
      },
      { enableHighAccuracy: true, timeout: 6000, maximumAge: 60_000 }
    );
  }, [deepLinkLocked, mapDrawerOpen, mapReadyVersion, safeDecode, searchParams]);

  // Normalize a chip entry to avoid duplicates from spacing/encoding variants
  const normEntry = React.useCallback((s: string): string => {
    return (s || '')
      .replace(/\+/g, ' ')
      .replace(/\s+/g, ' ')
      .replace(/\s*\/\s*/g, ' / ')
      .trim();
  }, []);

  // Debug requiredSkills state changes
  useEffect(() => {
    console.log('requiredSkills state changed:', requiredSkills);
  }, [requiredSkills]);

  // Debug appliedFilters state changes
  useEffect(() => {
    console.log('appliedFilters.requiredSkills changed:', appliedFilters.requiredSkills);
  }, [appliedFilters.requiredSkills]);

  // Use refs to track URL initialization and possible state restoration
  const isRestoringStateRef = React.useRef(false);
  const didInitFromUrlRef = React.useRef(false);

  // Handle URL parameters for search, category filtering and tags
  useEffect(() => {
    const searchParam = searchParams.get('search');
    const categoryParam = searchParams.get('category');
    const subParam = searchParams.get('sub');
    const tagsParam = searchParams.get('tags');
    const megaAll = searchParams.getAll('mega');
    const megaParam = searchParams.get('mega');

    // Handle search parameter
    if (searchParam !== null) {
      const decodedSearch = decodeURIComponent(searchParam);
      setSearchQuery(decodedSearch);
      setAppliedFilters(prev => ({
        ...prev,
        searchQuery: decodedSearch,
      }));
    }
    
    // Handle category parameter (show chip even if categories are not loaded or don't match)
    if (categoryParam) {
      const decodedCategoryParam = decodeURIComponent(categoryParam);
      const matchingCategory = categories.find(cat => cat.name.toLowerCase() === decodedCategoryParam.toLowerCase());
      const effectiveCategoryName = matchingCategory?.name || decodedCategoryParam;

      if (matchingCategory) {
        console.log('AllProductsPage - Setting category from URL:', { categoryParam, decodedCategoryParam, foundCategory: matchingCategory.name });
        setSelectedCategory(matchingCategory.name);
        setAppliedFilters(prev => ({ ...prev, selectedCategory: matchingCategory.name }));
      } else {
        // Fall back to raw category name for display (and light client-side filter)
        setSelectedCategory(effectiveCategoryName);
        setAppliedFilters(prev => ({ ...prev, selectedCategory: effectiveCategoryName }));
        console.log('AllProductsPage - No matching category found, falling back to URL value:', { categoryParam, decodedCategoryParam });
      }

      // Add pretty chip if sub present
      if (subParam) {
        const decodedSub = decodeURIComponent(subParam);
        const pretty = normEntry(`${effectiveCategoryName} / ${decodedSub}`);
        setAppliedFilters(prev => ({
          ...prev,
          megaCategories: Array.from(new Set([...(prev.megaCategories || []), pretty]))
        }));
        setUrlCategoryChip(pretty);
      }
    }
    
    // Handle pre-filled mega category chips from URL
    // Preferred: multiple 'mega' params, e.g., ?mega=Traditionell%20/%20Hindu&mega=Traditionell%20/%20Buddhistisch
    if (megaAll && megaAll.length > 0) {
      const decodedList = megaAll.map(s => normEntry(safeDecode(s))).filter(Boolean);
      if (decodedList.length > 0) {
        setAppliedFilters(prev => ({
          ...prev,
          megaCategories: Array.from(new Set([...(prev.megaCategories || []), ...decodedList]))
        }));
      }
    } else if (megaParam) {
      // Backward compatibility: single comma-joined value
      const decodedList = megaParam
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
        .map(s => normEntry(safeDecode(s)));
      if (decodedList.length > 0) {
        setAppliedFilters(prev => ({
          ...prev,
          megaCategories: Array.from(new Set([...(prev.megaCategories || []), ...decodedList]))
        }));
      }
    }

    // Handle tags parameter
    if (tagsParam) {
      const decodedTagsParam = decodeURIComponent(tagsParam);
      // Normalize to lowercase to ensure consistent server-side filtering
      const tagsArray = decodedTagsParam
        .split(',')
        .map(tag => tag.trim().toLowerCase())
        .filter(tag => tag.length > 0);
      console.log('AllProductsPage - Setting tags from URL:', tagsArray);
      setSelectedTags(tagsArray);
      setAppliedFilters(prev => ({
        ...prev,
        selectedTags: tagsArray
      }));
    }
    // Mark initialization complete to allow URL syncing thereafter
    didInitFromUrlRef.current = true;
  }, [searchParams, categories]);

  // If urlCategoryChip exists but isn't in megaCategories yet, promote it into mega and clear the temp chip
  useEffect(() => {
    if (urlCategoryChip) {
      const normalized = normEntry(urlCategoryChip);
      if (!(appliedFilters.megaCategories || []).includes(normalized)) {
        setAppliedFilters(prev => ({
          ...prev,
          megaCategories: Array.from(new Set([...(prev.megaCategories || []), normalized]))
        }));
      }
    }
  }, [urlCategoryChip]);

  // Keep URL in sync with current mega category chips; also normalize by removing category/sub when present
  useEffect(() => {
    // Do not sync URL until initial URL parsing has completed
    if (!didInitFromUrlRef.current) return;
    const params = new URLSearchParams(location.search);
    // If URL already has mega but state chips haven't been set yet, do not clear it
    if (params.has('mega') && (!appliedFilters.megaCategories || appliedFilters.megaCategories.length === 0)) {
      return;
    }
    // Normalize category/sub into mega representation
    if (params.has('category') || params.has('sub')) {
      params.delete('category');
      params.delete('sub');
    }
    const list = appliedFilters.megaCategories || [];
    // Reset all existing mega params
    params.delete('mega');
    if (list.length > 0) {
      list.forEach(v => params.append('mega', v));
    } else {
      params.delete('mega');
    }
    const newSearch = params.toString();
    navigate({ pathname: location.pathname, search: newSearch ? `?${newSearch}` : '' }, { replace: true });
  }, [appliedFilters.megaCategories]);

  // Icon map for top-level categories in the categories dialog
  const categoryIcons: Record<string, React.ReactNode> = React.useMemo(() => ({
    Damen: <WomanIcon />,
    Kinder: <ChildCareIcon />,
    Herren: <ManIcon />,
    Designerartikel: <DiamondIcon />,
    Elektronik: <DevicesIcon />,
    Unterhaltung: <MovieIcon />,
    'Hobby- & Sammlerartikel': <EmojiEventsIcon />,
    Traditionell: <MenuBookIcon />,
    Sport: <SportsSoccerIcon />,
    Home: <HomeIcon />,
  }), []);

  // Resolve an icon for known Traditionell sublabels
  const getSubIcon = React.useCallback((top: string, label: string): React.ReactNode | null => {
    if (top.toLowerCase() !== 'traditionell') return null;
    const l = label.toLowerCase();
    if (l.includes('christlich')) return <Church />;
    if (l.includes('orthodox')) return <Church />;
    if (l.includes('jüdisch') || l.includes('judisch')) return <Synagogue />;
    if (l.includes('islam')) return <Mosque />;
    if (l.includes('ramadan') || l.includes('eid')) return <NightsStay />;
    return null;
  }, []);

  // Load categories from database
  useEffect(() => {
    const fetchCategories = async () => {
      setLoadingCategories(true);
      setCategoriesError(null);
      try {
        const data = await CategoryService.getCategories();
        
        // Filter categories based on page type
        let filteredCategories;
        if (isJobsPage) {
          // On jobs page, show only job categories
          filteredCategories = data.filter(cat => cat.is_job_category);
        } else {
          // On products page, show only product categories
          filteredCategories = data.filter(cat => !cat.is_job_category);
        }
        
        setCategories(filteredCategories);
        console.log(`AllProductsPage - Loaded ${isJobsPage ? 'job' : 'product'} categories:`, filteredCategories);
      } catch (error) {
        console.error('Error fetching categories:', error);
        setCategoriesError('Failed to load categories');
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, [isJobsPage]);

  // Generate cache key based on current filters and pagination
  const generateCacheKey = (page: number, filters: typeof appliedFilters, pageSize: number) => {
    return JSON.stringify({
      page,
      pageSize,
      isJob: isJobsPage,
      search: filters.searchQuery,
      category: filters.selectedCategory,
      sortBy: filters.sortBy,
      priceRange: filters.priceRange,
      tags: filters.selectedTags,
      seller: filters.selectedSeller?.id,
      brands: Array.from(filters.selectedBrands),
      minRating: filters.minRating,
      condition: filters.selectedCondition,
      inStock: filters.showOnlyInStock,
      onSale: filters.showOnlyOnSale,
      excludeOwn: filters.excludeOwnProducts,
      purchaseType: filters.purchaseType,
      customFilters: {
        filter1: filters.customFilter1,
        filter2: filters.customFilter2,
        filter3: filters.customFilter3,
        filter4: filters.customFilter4,
        filter5: filters.customFilter5,
      }
    });
  };

  // Load products from database with server-side pagination and caching
  const fetchProducts = async (page: number = currentPage, sellerAlias?: string) => {
    setPageLoading(true);
    setProductsError(null);
    
    // Generate cache key
    const cacheKey = generateCacheKey(page, appliedFilters, itemsPerPage);
    
    // Check cache first
    const cachedData = productsCacheRef.current[cacheKey];
    if (cachedData) {
      const cacheAge = Date.now() - cachedData.timestamp;
      const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
      
      if (cacheAge < CACHE_DURATION) {
        console.log(`AllProductsPage - Using cached data for page ${page}`);
        setProducts(cachedData.products);
        setTotalProducts(cachedData.total);
        setTotalPages(Math.ceil(cachedData.total / itemsPerPage));
        setPageLoading(false);
        return;
      } else {
        // Remove expired cache entry
        delete productsCacheRef.current[cacheKey];
        console.log(`AllProductsPage - Cache expired for page ${page}, fetching fresh data`);
      }
    }
    
    try {
      // Build filter options
      const filterOptions: any = {
        page,
        limit: itemsPerPage,
        isActive: true,
        sellerAlias: sellerAlias,
        isJob: isJobsPage // Add job filter at server level
      };

      // Add search filter
      if (appliedFilters.searchQuery.trim()) {
        filterOptions.search = appliedFilters.searchQuery.trim();
      }

      // Add price range filter
      if (appliedFilters.priceRange[0] > 0 || appliedFilters.priceRange[1] < 2000) {
        filterOptions.minPrice = appliedFilters.priceRange[0];
        filterOptions.maxPrice = appliedFilters.priceRange[1];
      }

      // Add tag filter (only from applied filters, not local selection)
      if (appliedFilters.selectedTags && appliedFilters.selectedTags.length > 0) {
        // Send lowercase tags to match DB normalization and avoid case-sensitivity issues
        filterOptions.tags = appliedFilters.selectedTags.map(t => t.toLowerCase());
        console.log('AllProductsPage - Sending tags to server:', filterOptions.tags);
      }

      // Add sorting
      if (appliedFilters.sortBy === 'relevance') {
        filterOptions.sortBy = 'created_at';
        filterOptions.sortOrder = 'desc';
      } else if (appliedFilters.sortBy === 'price-low') {
        filterOptions.sortBy = 'price';
        filterOptions.sortOrder = 'asc';
      } else if (appliedFilters.sortBy === 'price-high') {
        filterOptions.sortBy = 'price';
        filterOptions.sortOrder = 'desc';
      } else if (appliedFilters.sortBy === 'newest') {
        filterOptions.sortBy = 'created_at';
        filterOptions.sortOrder = 'desc';
      } else if (appliedFilters.sortBy === 'oldest') {
        filterOptions.sortBy = 'created_at';
        filterOptions.sortOrder = 'asc';
      }

      const { products: fetchedProducts, total } = await ProductService.getProductsWithSellers(filterOptions);
      
      console.log(`AllProductsPage - Requested: page ${page}, limit ${itemsPerPage}, isJob: ${isJobsPage}`);
      console.log(`AllProductsPage - Received: ${fetchedProducts.length} products, total: ${total}`);
      console.log(`AllProductsPage - Setting totalProducts to: ${total}, totalPages to: ${Math.ceil(total / itemsPerPage)}`);
      
      // Cache the results
      productsCacheRef.current[cacheKey] = {
        products: fetchedProducts,
        total,
        timestamp: Date.now()
      };
      
      // Products are already filtered by job/product type at server level
      setProducts(fetchedProducts);
      setTotalProducts(total);
      setTotalPages(Math.ceil(total / itemsPerPage));
    } catch (error) {
      console.error('Error fetching products:', error);
      setProductsError('Failed to load products');
      setProducts([]);
      setTotalProducts(0);
      setTotalPages(0);
    } finally {
      setPageLoading(false);
    }
  };

  // Fetch products when applied filters change
  useEffect(() => {
    console.log('AllProductsPage - useEffect triggered for appliedFilters change:', {
      appliedFilters
    });
    // Clear cache when filters change to ensure fresh data
    productsCacheRef.current = {};
    fetchProducts(currentPage);
  }, [appliedFilters]);

  // Fetch products when page number or page size changes
  useEffect(() => {
    fetchProducts(currentPage);
  }, [currentPage, itemsPerPage]);

  // Clear cache when component unmounts or when leaving the products page
  useEffect(() => {
    return () => {
      console.log('AllProductsPage - Clearing products cache on unmount');
      productsCacheRef.current = {};
    };
  }, []);

  // Load platform fee settings on mount
  useEffect(() => {
    const loadFeeSettings = async () => {
      try {
        const settings = await PlatformSettingsService.getPlatformFeeSettings();
        setPlatformFeeSettings(settings);
      } catch (error) {
        console.error('Error loading platform fee settings:', error);
        // Set default fee settings if loading fails
        setPlatformFeeSettings({
          fee_type: 'fixed',
          fee_amount: 0.50,
          fee_percentage: null,
        });
      }
    };
    loadFeeSettings();
  }, []);

  // Clear cache when switching between products and jobs pages
  useEffect(() => {
    console.log('AllProductsPage - Clearing cache due to page type change');
    productsCacheRef.current = {};
  }, [isJobsPage]);

  // Reset to page 1 when filters change (except when restoring state)
  useEffect(() => {
    // Only reset if we're not already on page 1 and filters have changed
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [priceRange, sortBy, appliedFilters.selectedTags, appliedFilters.megaCategories]);

  // Auto-clear filters when switching between Products and Jobs tabs
  useEffect(() => {
    // Clear all filters when switching tabs
    setSearchQuery('');
    setSelectedCategory('All Categories');
    setSortBy('relevance');
    setPriceRange([0, 2000]);
    setPurchaseType('any');
    setSelectedCondition('all');
    setShowOnlyInStock(false);
    setShowOnlyOnSale(false);
    setExcludeOwnProducts(false);
    setSelectedBrands(new Set());
    setMinRating(0);
    setSelectedSeller(null);
    setSellerSearchQuery('');
    
    // Update applied filters to match the cleared state
    setAppliedFilters({
      searchQuery: '',
      selectedCategory: 'All Categories',
      sortBy: 'relevance',
      priceRange: [0, 2000],
      salaryType: 'any',
      selectedCondition: 'all',
      showOnlyInStock: false,
      showOnlyOnSale: false,
      excludeOwnProducts: false,
      selectedBrands: new Set(),
      minRating: 0,
      selectedSeller: null,
      purchaseType: 'any',
      requiredSkills: [],
      selectedTags: [],
      megaCategories: [],
      customFilter1: '',
      customFilter2: '',
      customFilter3: '',
      customFilter4: '',
      customFilter5: '',
    });

    // Show notification briefly
    setShowAutoClearNotification(true);
    const timer = setTimeout(() => {
      setShowAutoClearNotification(false);
    }, 3000); // Hide after 3 seconds

    return () => clearTimeout(timer);
  }, [isJobsPage]);

  // Load favorites when user is authenticated and products are loaded
  useEffect(() => {
    const loadFavorites = async () => {
      if (isAuthenticated && user?.id && products.length > 0) {
        try {
          const productIds = products.map(product => product.id);
          const favoriteStatuses = isJobsPage
            ? await FavoritesService.getJobFavoriteStatuses(productIds, user.id)
            : await FavoritesService.getFavoriteStatuses(productIds, user.id);
          
          const favoriteSet = new Set<string>();
          Object.entries(favoriteStatuses).forEach(([productId, isFavorited]) => {
            if (isFavorited) {
              favoriteSet.add(productId);
            }
          });
          
          setFavorites(favoriteSet);
        } catch (error) {
          console.error('Error loading favorites:', error);
        }
      }
    };

    loadFavorites();
  }, [isAuthenticated, user?.id, products, isJobsPage]);

  // Translate product names dynamically when language or products change
  useEffect(() => {
    const run = async () => {
      if (!autoTranslate || language === 'en' || products.length === 0) {
        setTranslatedNames({});
        return;
      }
      try {
        const entries = await Promise.all(
          products.map(async (p) => {
            const translated = await translateText(p.name || 'Untitled', language, 'en');
            return [p.id, translated] as const;
          })
        );
        const map: Record<string, string> = {};
        entries.forEach(([id, name]) => (map[id] = name));
        setTranslatedNames(map);
      } catch (e) {
        console.error('Error translating product names:', e);
        setTranslatedNames({});
      }
    };
    run();
  }, [products, language, autoTranslate]);

  // Translate category names for dropdown when language or categories change
  useEffect(() => {
    const run = async () => {
      if (!autoTranslate || language === 'en' || categories.length === 0) {
        setTranslatedCategoryNames({});
        return;
      }
      try {
        const entries = await Promise.all(
          categories.map(async (c) => {
            const translated = await translateText(c.name || 'Untitled', language, 'en');
            return [c.name, translated] as const;
          })
        );
        const map: Record<string, string> = {};
        entries.forEach(([orig, tr]) => (map[orig] = tr));
        setTranslatedCategoryNames(map);
      } catch (e) {
        console.error('Error translating category names:', e);
        setTranslatedCategoryNames({});
      }
    };
    run();
  }, [categories, language, autoTranslate]);

  // Listen for favorite changes coming from detail page and update local set
  useEffect(() => {
    const onFavoriteChanged = (e: any) => {
      const { id, isFavorited, isJob } = e.detail || {};
      if (!id) return;
      if (isJobsPage !== !!isJob) return;
      setFavorites(prev => {
        const next = new Set(prev);
        if (isFavorited) next.add(id); else next.delete(id);
        return next;
      });
    };
    window.addEventListener('favorite-changed', onFavoriteChanged as EventListener);
    return () => window.removeEventListener('favorite-changed', onFavoriteChanged as EventListener);
  }, [isJobsPage]);

  // Seller search functionality
  useEffect(() => {
    const searchSellers = async () => {
      if (sellerSearchQuery.length < 2) {
        setSellerSearchResults([]);
        return;
      }

      setLoadingSellerSearch(true);
      setSellerSearchError(null);
      
      try {
        const results = await UserService.searchUsersByAlias(sellerSearchQuery);
        setSellerSearchResults(results);
      } catch (error) {
        console.error('Error searching sellers:', error);
        setSellerSearchError('Failed to search sellers');
      } finally {
        setLoadingSellerSearch(false);
      }
    };

    const timeoutId = setTimeout(searchSellers, 300); // Debounce search
    return () => clearTimeout(timeoutId);
  }, [sellerSearchQuery]);

  // Restore filters from navigation state
  React.useEffect(() => {
    if (location.state?.preserveFilters) {
      console.log('Restoring state from navigation:', location.state);
      isRestoringStateRef.current = true;
      const { page, filters } = location.state;
      const restoredMapOpen = (location.state as any)?.mapDrawerOpen;
      if (filters) {
        // Set all states at once to minimize re-renders
        setSearchQuery(filters.searchQuery || '');
        setSelectedCategory(filters.selectedCategory || 'All Categories');
        setSortBy(filters.sortBy || 'relevance');
        setPriceRange(filters.priceRange || [0, 2000]);
        setSelectedCondition(filters.selectedCondition || 'all');
        setShowOnlyInStock(filters.showOnlyInStock || false);
        setSelectedBrands(new Set(filters.selectedBrands || []));
        setMinRating(filters.minRating || 0);
        setViewMode(isJobsPage ? (filters.viewMode || 'grid') : 'grid');
        setCurrentPage(page || 1);
        console.log('Restored page to:', page || 1);
      }

      if (typeof restoredMapOpen !== 'undefined') {
        setMapDrawerOpen(!!restoredMapOpen);
      }
      // Reset the flag after state updates complete
      setTimeout(() => {
        isRestoringStateRef.current = false;
        console.log('State restoration complete');
      }, 300);
    }
  }, [location.state]);

  // Transform ProductFromService to ProductCard format
  const transformProductForCard = (product: ProductFromService) => {
    if (!product) {
      return {
        id: '',
        name: 'Invalid Product',
        price: 0,
        image: '/placeholder-product.jpg',
        rating: 0,
        reviewCount: 0,
        category: 'Uncategorized',
        seller: { name: 'Unknown Seller', avatar: '', location: '' },
        isNew: false,
        isFeatured: false,
        description: '',
        tags: [],
        isJob: false,
      };
    }
    
    // Calculate price with platform fees (only for products, not jobs)
    const basePrice = product.price || 0;
    const isJob = product.isJob || false;
    const priceWithFees = (!isJob && platformFeeSettings)
      ? calculatePriceWithFeesSync(basePrice, platformFeeSettings)
      : basePrice;
    
    const baseProduct = {
      id: product.id,
      name: (autoTranslate && translatedNames[product.id]) ? translatedNames[product.id] : (product.name || 'Untitled'),
      price: priceWithFees, // Use price with fees included (only for products)
      originalPrice: product.originalPrice ? (!isJob && platformFeeSettings
        ? calculatePriceWithFeesSync(product.originalPrice, platformFeeSettings)
        : product.originalPrice) : undefined,
      image: product.image || '/placeholder-product.jpg',
      images: Array.isArray((product as any).images) ? (product as any).images : (product.image ? [product.image] : []),
      rating: product.rating || 0,
      reviewCount: product.reviewCount || 0,
      category: product.category || 'Uncategorized',
      seller: {
        name: product.seller?.name || 'Unknown Seller',
        avatar: product.seller?.avatar || '',
        location: product.seller?.location || '',
      },
      isNew: product.isNew || false,
      isFeatured: product.isFeatured || false,
      discount: product.discount,
      description: product.description || '',
      tags: product.tags || [],
      isJob: product.isJob || false,
      // Job-specific fields
      company: product.company || product.seller?.name || 'Unknown Company',
      companyLogo: product.companyLogo || product.seller?.avatar || '',
      location: product.location || 'Unknown Location',
      salary: product.salary || undefined,
      employmentType: product.employmentType || 'full-time',
      experienceLevel: product.experienceLevel || 'mid',
      isRemote: product.isRemote || false,
      postedDate: product.postedDate || new Date().toISOString(),
      requirements: product.requirements || [],
      benefits: product.benefits || [],
      duration: product.duration || undefined,
      applicantsCount: product.applicantsCount || Math.floor(Math.random() * 50),
      viewsCount: product.viewsCount || Math.floor(Math.random() * 200),
      isUrgent: product.isUrgent || false,
      requiredSkills: product.requiredSkills || [],
      // Auction fields (optional in products listing)
      auctionId: (product as any).auctionId,
      auction: (product as any).auction,
      // Private single buy flag
      is_private_singlebuy: product.is_private_singlebuy ?? false,
      // Product type and negotiable flag (used for Kaufart labels/filters)
      productType: (product as any).productType ?? (product as any).product_type,
      isNegotiable: (product as any).isNegotiable ?? (product as any).is_negotiable,
      // Preserve sellerId
      sellerId: product.sellerId,
    } as any;

    if (product.isJob) {
      return {
        ...baseProduct,
        // carry categories array if present (harmless for jobs)
        ...(Array.isArray((product as any).categories) ? { categories: (product as any).categories } : {}),
        // Job-specific fields - these would come from the database
        company: product.company || product.seller?.name || 'Unknown Company',
        companyLogo: product.companyLogo || product.seller?.avatar || '',
        location: product.location || 'Unknown Location',
        salary: product.salary || undefined,
        employmentType: product.employmentType || 'full-time',
        experienceLevel: product.experienceLevel || 'mid',
        isRemote: product.isRemote || false,
        postedDate: product.postedDate || new Date().toISOString(),
        requirements: product.requirements || [],
        benefits: product.benefits || [],
        duration: product.duration || undefined,
        applicantsCount: product.applicantsCount || Math.floor(Math.random() * 50),
        viewsCount: product.viewsCount || Math.floor(Math.random() * 200),
        isUrgent: product.isUrgent || false,
      };
    }

    // Non-job product
    return {
      ...baseProduct,
      ...(Array.isArray((product as any).categories) ? { categories: (product as any).categories } : {}),
    };
  };

  // Get unique brands from products
  const brands = useMemo(() => {
    const brandSet = new Set(products.map(product => product.brand).filter((brand): brand is string => Boolean(brand)));
    return Array.from(brandSet).sort();
  }, [products]);

  const getSortLabel = (value: string) => {
    switch (value) {
      case 'relevance':
        return t('allProducts.sort.relevance');
      case 'price-low':
        return t('allProducts.sort.priceLow');
      case 'price-high':
        return t('allProducts.sort.priceHigh');
      case 'rating':
        return t('allProducts.sort.rating');
      case 'newest':
        return t('allProducts.sort.newest');
      case 'popular':
        return t('allProducts.sort.popular');
      default:
        return value;
    }
  };

  // Filter and sort products
  const filteredProducts = useMemo(() => {
    // Helper: for auctions, compare using current highest bid; otherwise product price
    const getComparablePrice = (p: any): number => {
      const auctionBid = p?.auction?.current_bid;
      return typeof auctionBid === 'number' && !isNaN(auctionBid) ? auctionBid : p.price;
    };
    let filtered = products.filter(product => {
      // Job/Product filter based on URL
      if (isJobsPage && !product.isJob) {
        return false;
      }
      if (!isJobsPage && product.isJob) {
        return false;
      }

      // Search query
      if (appliedFilters.searchQuery && !product.name.toLowerCase().includes(appliedFilters.searchQuery.toLowerCase()) &&
          !product.description?.toLowerCase().includes(appliedFilters.searchQuery.toLowerCase()) &&
          !product.tags?.some(tag => tag.toLowerCase().includes(appliedFilters.searchQuery.toLowerCase()))) {
        return false;
      }

      // Tag filter (client-side backup in case server-side fails)
      if (appliedFilters.selectedTags && appliedFilters.selectedTags.length > 0) {
        const productTags = product.tags || [];
        const hasMatchingTag = appliedFilters.selectedTags.some(selectedTag => 
          productTags.some(productTag => 
            productTag.toLowerCase() === selectedTag.toLowerCase()
          )
        );
        if (!hasMatchingTag) {
          return false;
        }
      }

      // Category filter (supports categories[] paths + URL params + legacy single category)
      {
        const norm = (s: any) => (typeof s === 'string' ? s : '').trim().toLowerCase();
        // Build required path(s)
        let requiredPaths: string[] = [];
        if (appliedFilters.megaCategories && appliedFilters.megaCategories.length > 0) {
          requiredPaths = appliedFilters.megaCategories.map(norm);
        } else {
          const params = new URLSearchParams(location.search);
          const main = norm(params.get('category'));
          const sub = norm(params.get('sub'));
          const subsub = norm(params.get('subsub'));
          if (main) {
            const path = [main, sub, subsub].filter(Boolean).join(' / ');
            if (path) requiredPaths = [path];
          }
        }

        if (requiredPaths.length > 0) {
          const productPaths: string[] = Array.isArray((product as any).categories)
            ? (product as any).categories
            : [];
          const productPathsNorm = productPaths.map(norm);
          const matches = requiredPaths.some(req =>
            productPathsNorm.some(p => p === req || p.startsWith(req + ' / '))
          );
          if (!matches) return false;
        } else if (appliedFilters.selectedCategory !== 'All Categories') {
          // Fallback legacy single-category behavior
          if (product.category !== appliedFilters.selectedCategory) return false;
        }
      }

      // Product-only: Purchase type filter
      if (!isJobsPage && appliedFilters.purchaseType !== 'any') {
        const pt = (product as any)?.productType ?? (product as any)?.product_type;
        const isAuction = pt === 'a' || Boolean(product?.auction?.id || product?.auctionId);
        const isOffer = pt === 'p' || Boolean((product as any)?.is_private_singlebuy);
        const isNegotiable = Boolean((product as any)?.isNegotiable ?? (product as any)?.is_negotiable);
        const isDirect = (pt === 'd') ? !isNegotiable : (!isAuction && !isOffer && !isNegotiable);
        if (appliedFilters.purchaseType === 'auction' && !isAuction) return false;
        if (appliedFilters.purchaseType === 'direct' && !isDirect) return false;
        if (appliedFilters.purchaseType === 'negotiable' && !isNegotiable) return false;
        if (appliedFilters.purchaseType === 'offer' && !isOffer) return false;
      }

      // Price range filter (use current auction bid when available)
      const comparablePrice = getComparablePrice(product);
      if (comparablePrice < appliedFilters.priceRange[0] || comparablePrice > appliedFilters.priceRange[1]) {
        return false;
      }

      // Condition filter
      if (appliedFilters.selectedCondition !== 'all' && product.condition !== appliedFilters.selectedCondition) {
        return false;
      }

      // In stock filter
      if (appliedFilters.showOnlyInStock && !product.inStock) {
        return false;
      }

      // On sale filter
      if (appliedFilters.showOnlyOnSale && !product.originalPrice) {
        return false;
      }

      // Exclude own products filter
      if (appliedFilters.excludeOwnProducts && user?.id && product.sellerId === user.id) {
        return false;
      }

      // Brand filter
      if (appliedFilters.selectedBrands.size > 0 && product.brand && !appliedFilters.selectedBrands.has(product.brand)) {
        return false;
      }

      // Rating filter
      if (product.rating < appliedFilters.minRating) {
        return false;
      }

      // Seller filter
      if (appliedFilters.selectedSeller) {
        console.log('Checking seller filter:', {
          selectedSeller: appliedFilters.selectedSeller,
          productSellerId: product.sellerId,
          productSellerName: product.seller.name,
        });
        
        // Check if it's a typed alias (starts with 'typed-') or exact seller ID match
        if (appliedFilters.selectedSeller.id.startsWith('typed-')) {
          // For typed aliases, check if the seller name (alias) matches exactly
          const typedAlias = appliedFilters.selectedSeller.alias.toLowerCase();
          const sellerAlias = product.seller.name.toLowerCase();
          console.log('Typed alias check:', { typedAlias, sellerAlias, matches: sellerAlias === typedAlias });
          if (sellerAlias !== typedAlias) {
            return false;
          }
        } else {
          // For selected sellers, check exact seller ID match
          console.log('Exact seller ID check:', { 
            selectedId: appliedFilters.selectedSeller.id, 
            productId: product.sellerId, 
            matches: product.sellerId === appliedFilters.selectedSeller.id 
          });
          if (product.sellerId !== appliedFilters.selectedSeller.id) {
            return false;
          }
        }
      }

      // Required skills filter (only for jobs)
      if (isJobsPage && appliedFilters.requiredSkills.length > 0) {
        const productSkills = product.requiredSkills || [];
        const hasMatchingSkill = appliedFilters.requiredSkills.some(filterSkill =>
          productSkills.some((productSkill: string) =>
            productSkill.toLowerCase().includes(filterSkill.toLowerCase()) ||
            filterSkill.toLowerCase().includes(productSkill.toLowerCase())
          )
        );
        if (!hasMatchingSkill) {
          return false;
        }
      }

      // Custom filter 1 - Job Type filter (only for jobs)
      if (isJobsPage && appliedFilters.customFilter1) {
        const selectedJobTypes = appliedFilters.customFilter1.split(',').map(v => v.trim()).filter(v => v);
        if (selectedJobTypes.length > 0) {
          const productJobType = product.employmentType?.toLowerCase() || '';
          const hasMatchingJobType = selectedJobTypes.some(jobType =>
            productJobType.includes(jobType.toLowerCase()) ||
            jobType.toLowerCase().includes(productJobType)
          );
          if (!hasMatchingJobType) {
            return false;
          }
        }
      }

      // Custom filter 2 - Experience Level filter (only for jobs)
      if (isJobsPage && appliedFilters.customFilter2) {
        const selectedExperienceLevels = appliedFilters.customFilter2.split(',').map(v => v.trim()).filter(v => v);
        if (selectedExperienceLevels.length > 0) {
          const productExperienceLevel = product.experienceLevel?.toLowerCase() || '';
          const hasMatchingExperienceLevel = selectedExperienceLevels.some(experienceLevel =>
            productExperienceLevel.includes(experienceLevel.toLowerCase()) ||
            experienceLevel.toLowerCase().includes(productExperienceLevel)
          );
          if (!hasMatchingExperienceLevel) {
            return false;
          }
        }
      }

      // Custom filter 3 - Required Skills filter (only for jobs)
      if (isJobsPage && appliedFilters.customFilter3) {
        const selectedSkills = appliedFilters.customFilter3.split(',').map(v => v.trim()).filter(v => v);
        if (selectedSkills.length > 0) {
          const productSkills = product.requiredSkills || [];
          const productTags = product.tags || [];
          
          // Debug logging
          console.log('Required Skills Filter Debug:', {
            selectedSkills,
            productSkills,
            productTags,
            productName: product.name
          });
          
          // Check both requiredSkills and tags arrays
          const hasMatchingSkill = selectedSkills.some(skill =>
            productSkills.some((productSkill: string) =>
              productSkill.toLowerCase().includes(skill.toLowerCase()) ||
              skill.toLowerCase().includes(productSkill.toLowerCase())
            ) ||
            productTags.some((productTag: string) =>
              productTag.toLowerCase().includes(skill.toLowerCase()) ||
              skill.toLowerCase().includes(productTag.toLowerCase())
            )
          );
          
          console.log('Has matching skill:', hasMatchingSkill);
          
          if (!hasMatchingSkill) {
            return false;
          }
        }
      }

      // Custom filter 4 - Benefits filter (only for jobs)
      if (isJobsPage && appliedFilters.customFilter4) {
        const selectedBenefits = appliedFilters.customFilter4.split(',').map(v => v.trim()).filter(v => v);
        if (selectedBenefits.length > 0) {
          const productBenefits = product.benefits || [];
          const productDescription = product.description?.toLowerCase() || '';
          
          // Debug logging
          console.log('Benefits Filter Debug:', {
            selectedBenefits,
            productBenefits,
            productDescription,
            productName: product.name
          });
          
          // Check both benefits array and description
          const hasMatchingBenefit = selectedBenefits.some(benefit =>
            productBenefits.some((productBenefit: string) =>
              productBenefit.toLowerCase().includes(benefit.toLowerCase()) ||
              benefit.toLowerCase().includes(productBenefit.toLowerCase())
            ) ||
            productDescription.includes(benefit.toLowerCase())
          );
          
          console.log('Has matching benefit:', hasMatchingBenefit);
          
          if (!hasMatchingBenefit) {
            return false;
          }
        }
      }

      // Custom filter 5 - City/Location filter (only for jobs)
      if (isJobsPage && appliedFilters.customFilter5) {
        const selectedLocations = appliedFilters.customFilter5.split(',').map(v => v.trim()).filter(v => v);
        if (selectedLocations.length > 0) {
          const productLocation = product.location?.toLowerCase() || '';
          const productDescription = product.description?.toLowerCase() || '';
          
          // Debug logging
          console.log('City/Location Filter Debug:', {
            selectedLocations,
            productLocation,
            productDescription,
            productName: product.name
          });
          
          // Check both location field and description
          const hasMatchingLocation = selectedLocations.some(location =>
            productLocation.includes(location.toLowerCase()) ||
            location.toLowerCase().includes(productLocation) ||
            productDescription.includes(location.toLowerCase())
          );
          
          console.log('Has matching location:', hasMatchingLocation);
          
          if (!hasMatchingLocation) {
            return false;
          }
        }
      }

      return true;
    });

    // Sort products
    switch (appliedFilters.sortBy) {
      case 'price-low':
        filtered.sort((a, b) => {
          const aPrice = getComparablePrice(a);
          const bPrice = getComparablePrice(b);
          return aPrice - bPrice;
        });
        break;
      case 'price-high':
        filtered.sort((a, b) => {
          const aPrice = getComparablePrice(a);
          const bPrice = getComparablePrice(b);
          return bPrice - aPrice;
        });
        break;
      case 'rating':
        filtered.sort((a, b) => b.rating - a.rating);
        break;
      case 'newest':
        filtered.sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
        break;
      case 'popular':
        filtered.sort((a, b) => b.reviewCount - a.reviewCount);
        break;
      default:
        // Relevance - keep original order for now
        break;
    }

    return filtered;
  }, [
    products,
    appliedFilters,
    isJobsPage,
    user?.id,
    productsError,
  ]);

  const defaultJobDetailId = React.useMemo(() => {
    if (!isJobsPage) return null;
    const first = (filteredProducts || [])[0];
    if (!first) return null;
    return String((first as any).id || '').trim() || null;
  }, [filteredProducts, isJobsPage]);

  const rightSidePanelOpen = isJobsPage && jobInfoPanelOpen;
  const previewPanelWidthPx = 640;

  React.useEffect(() => {
    try {
      const root = document.documentElement;
      const shouldOffset = !isMobile && rightSidePanelOpen;
      root.style.setProperty('--right-panel-offset', shouldOffset ? `${previewPanelWidthPx}px` : '0px');
      root.setAttribute('data-right-panel-open', shouldOffset ? 'true' : 'false');

      try {
        window.dispatchEvent(new CustomEvent('right-panel-open-changed', { detail: { open: shouldOffset } }));
      } catch {
        // ignore
      }

      return () => {
        root.style.setProperty('--right-panel-offset', '0px');
        root.removeAttribute('data-right-panel-open');

        try {
          window.dispatchEvent(new CustomEvent('right-panel-open-changed', { detail: { open: false } }));
        } catch {
          // ignore
        }
      };
    } catch {
      return;
    }
  }, [isMobile, previewPanelWidthPx, rightSidePanelOpen]);

  const paginatedProducts = filteredProducts;

  // Handlers
  const handleToggleFavorite = async (productId: string) => {
    // Check if user is authenticated
    if (!isAuthenticated || !user?.id) {
      setSignInModalOpen(true);
      return;
    }
    
    // Prevent multiple rapid clicks
    if (favoritesLoading) return;
    
    setFavoritesLoading(true);
    
    try {
      // Toggle favorite in appropriate table (jobs vs products)
      const isNowFavorited = isJobsPage
        ? await FavoritesService.toggleJobFavorite(productId, user.id)
        : await FavoritesService.toggleFavorite(productId, user.id);
      
      // Update local state
      setFavorites(prev => {
        const newFavorites = new Set(prev);
        if (isNowFavorited) {
          newFavorites.add(productId);
        } else {
          newFavorites.delete(productId);
        }
        return newFavorites;
      });
    } catch (error) {
      console.error('Error toggling favorite:', error);
      const err: any = error;
      const details = err?.message || err?.error_description || err?.details || err?.hint || String(err);
      // Show a richer error if toast is available on this page later
      console.warn('Favorite toggle failed:', details);
    } finally {
      setFavoritesLoading(false);
    }
  };

  const handleAddToCart = async (productId: string) => {
    try {
      const raw = products.find((p) => p.id === productId);
      if (!raw) {
        toast.error('Product not found');
        return;
      }

      // Compute per-item shipping from product delivery_option_ids (cheapest option)
      let shippingPerItem = 0;
      try {
        const { data: prodRow, error: prodErr } = await supabase
          .from('products')
          .select('delivery_option_ids')
          .eq('id', productId)
          .single();
        if (!prodErr) {
          const ids = Array.isArray((prodRow as any)?.delivery_option_ids) ? (prodRow as any).delivery_option_ids : [];
          if (ids.length > 0) {
            const all = await DeliveryOptionsService.getAll('DE');
            const filtered = all.filter((o) => ids.includes(o.id));
            shippingPerItem = filtered.reduce((min, opt) => {
              const p = typeof opt.price_eur === 'number' && !isNaN(opt.price_eur) ? opt.price_eur : null;
              if (p === null) return min;
              if (min === null) return p;
              return Math.min(min, p);
            }, null as number | null) || 0;
          }
        }
      } catch {
        // ignore shipping failures; item can still be added
      }

      const card = transformProductForCard(raw) as any;
      const images: string[] = Array.isArray(card.images) ? card.images : (card.image ? [card.image] : []);

      const cartProduct: CartProduct = {
        id: card.id,
        title: String(card.name || raw.name || 'Untitled'),
        description: String(card.description || raw.description || ''),
        price: Number(card.price || 0),
        originalPrice: card.originalPrice ? Number(card.originalPrice) : undefined,
        images: images.length > 0 ? images : ['/placeholder-product.jpg'],
        category: String(card.category || raw.category || 'Uncategorized'),
        subcategory: undefined,
        brand: String(card.brand || (raw as any).brand || 'Unknown'),
        size: Array.isArray((card as any).sizes) ? (card as any).sizes : ['One Size'],
        color: Array.isArray((card as any).colors) ? (card as any).colors : [],
        material: String((raw as any).material || 'Not specified'),
        condition: ((raw as any).condition || 'new') as any,
        stock: (raw as any).inStock === false ? 0 : 1,
        rating: Number(raw.rating || 0),
        reviewCount: Number(raw.reviewCount || 0),
        sellerId: String(raw.sellerId || (raw as any).seller_id || ''),
        seller: {
          id: String(raw.sellerId || (raw as any).seller_id || ''),
          name: String((raw as any).seller?.name || 'Seller'),
          rating: 0,
          reviewCount: 0,
        },
        tags: Array.isArray(raw.tags) ? raw.tags : [],
        isActive: true,
        isJob: !!raw.isJob,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      addItem(cartProduct, 1, undefined, undefined, shippingPerItem);
      toast.success('Added to cart', { position: 'top-center' });
    } catch (e: any) {
      console.error('Add to cart failed:', e);
      toast.error(e?.message || 'Failed to add to cart');
    }
  };

  const handleViewDetails = (productId: string, navigationState?: any) => {
    navigate(`/product/${productId}`, {
      state: {
        fromAllProducts: true,
        page: currentPage,
        mapDrawerOpen,
        filters: {
          searchQuery,
          selectedCategory,
          sortBy,
          priceRange,
          selectedCondition,
          showOnlyInStock,
          selectedBrands: Array.from(selectedBrands),
          minRating,
          viewMode
        }
      }
    });
  };

  const handleShare = (productId: string) => {
    const raw = products.find((p) => p.id === productId);
    const title = raw ? String((raw as any).name || (raw as any).title || 'Product') : undefined;
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const url = origin ? `${origin}/product/${encodeURIComponent(productId)}` : `/product/${encodeURIComponent(productId)}`;
    setSharePayload({ id: productId, url, title });
    setShareDialogOpen(true);
  };

  const handleApplyFilters = () => {
    // If user has typed a seller alias but no seller is selected, create one
    let finalSelectedSeller = selectedSeller;
    if (sellerSearchQuery && !selectedSeller) {
      finalSelectedSeller = {
        id: `typed-${sellerSearchQuery}`,
        alias: sellerSearchQuery,
        first_name: null,
        last_name: null,
        email: '',
        created_at: new Date().toISOString(),
      };
      setSelectedSeller(finalSelectedSeller);
    }
    
    console.log('Applying filters:', {
      searchQuery,
      selectedCategory,
      selectedSeller: finalSelectedSeller,
      selectedBrands: Array.from(selectedBrands),
      priceRange,
      selectedCondition,
      showOnlyInStock,
      showOnlyOnSale,
      excludeOwnProducts,
      minRating,
      purchaseType,
      requiredSkills,
      selectedTags,
    });
    
    // Reset pagination to page 1 when applying filters
    setCurrentPage(1);

    // Keep URL in sync (preserve map/place and other params)
    try {
      const params = new URLSearchParams(location.search);
      const q = (searchQuery || '').trim();
      if (q) {
        params.set('search', q);
      } else {
        params.delete('search');
      }
      navigate(
        { pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : '' },
        { replace: true }
      );
    } catch {
      // ignore
    }
    
    // Apply filters immediately to the appliedFilters state
    setAppliedFilters({
      searchQuery,
      selectedCategory,
      sortBy,
      priceRange,
      salaryType,
      selectedCondition,
      showOnlyInStock,
      showOnlyOnSale,
      excludeOwnProducts,
      selectedBrands,
      minRating,
      selectedSeller: finalSelectedSeller,
      purchaseType,
      requiredSkills,
      // Normalize tags to lowercase for consistent filtering
      selectedTags: selectedTags.map(t => t.toLowerCase()),
      megaCategories: appliedFilters.megaCategories || [],
      customFilter1,
      customFilter2,
      customFilter3,
      customFilter4,
      customFilter5,
    });
    
    // Also update the local state to match applied filters
    setSearchQuery(searchQuery);
    setSelectedCategory(selectedCategory);
    setSortBy(sortBy);
    setPriceRange(priceRange);
    setSalaryType(salaryType);
    setSelectedCondition(selectedCondition);
    setShowOnlyInStock(showOnlyInStock);
    setShowOnlyOnSale(showOnlyOnSale);
    setExcludeOwnProducts(excludeOwnProducts);
    setSelectedBrands(selectedBrands);
    setMinRating(minRating);
    setSelectedSeller(finalSelectedSeller);
    setRequiredSkills(requiredSkills);
    // Keep local selectedTags normalized to lowercase too
    setSelectedTags(selectedTags.map(t => t.toLowerCase()));
    
    setFilterDrawerOpen(false); // Close the filter drawer
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('All Categories');
    setSortBy('relevance');
    setPriceRange([0, 2000]);
    setPurchaseType('any');
    setSalaryType('any');
    setSelectedCondition('all');
    setShowOnlyInStock(false);
    setShowOnlyOnSale(false);
    setExcludeOwnProducts(false);
    setSelectedBrands(new Set());
    setMinRating(0);
    setSelectedSeller(null);
    setSellerSearchQuery('');
    setRequiredSkills([]);
    setSelectedTags([]);
    setCustomFilter1('');
    setCustomFilter2('');
    setCustomFilter3('');
    setCustomFilter4('');
    setCustomFilter5('');
    
    // Keep URL in sync (clear product search but preserve map/place)
    try {
      const params = new URLSearchParams(location.search);
      params.delete('search');
      navigate(
        { pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : '' },
        { replace: true }
      );
    } catch {
      // ignore
    }

    // Also clear applied filters
    setAppliedFilters({
      searchQuery: '',
      selectedCategory: 'All Categories',
      sortBy: 'relevance',
      priceRange: [0, 2000],
      salaryType: 'any',
      selectedCondition: 'all',
      showOnlyInStock: false,
      showOnlyOnSale: false,
      excludeOwnProducts: false,
      selectedBrands: new Set(),
      minRating: 0,
      selectedSeller: null,
      purchaseType: 'any',
      requiredSkills: [],
      selectedTags: [],
      megaCategories: [],
      customFilter1: '',
      customFilter2: '',
      customFilter3: '',
      customFilter4: '',
      customFilter5: '',
    });
    
    // Refresh products without filters
    fetchProducts(currentPage);
  };

  const handleBrandToggle = (brand: string) => {
    setSelectedBrands(prev => {
      const newBrands = new Set(prev);
      if (newBrands.has(brand)) {
        newBrands.delete(brand);
      } else {
        newBrands.add(brand);
      }
      return newBrands;
    });
  };

  // Reset page when filters change (but not when restoring state)
  useEffect(() => {
    if (!isRestoringStateRef.current) {
      console.log('Resetting page to 1 due to filter change');
      setCurrentPage(1);
    } else {
      console.log('Skipping page reset - restoring state');
    }
  }, [selectedCategory, sortBy, priceRange, selectedCondition, showOnlyInStock, showOnlyOnSale, selectedBrands, minRating, appliedFilters.selectedTags]);


  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* Header */}
      <Box sx={{ bgcolor: 'background.paper', borderBottom: theme => `1px solid ${theme.palette.divider}`, mb: 2 }}>
        <Container maxWidth="xl" sx={{ pr: { md: 'var(--right-panel-offset, 0px)' }, transition: 'padding-right 0.2s ease' }}>
          <Box sx={{ py: 1.5 }}>
            {showAutoClearNotification && (
              <Typography variant="body2" color="primary" sx={{ mb: 2, fontStyle: 'italic' }}>
                {isJobsPage ? t('allProducts.notice.jobs') : t('allProducts.notice.products')}
              </Typography>
            )}

            {!(isMobile && mapDrawerOpen) && (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 0, 
                  flex: 1, 
                  minWidth: 300,
                  alignItems: 'center',
                  background: theme => theme.palette.background.paper,
                  borderRadius: 2,
                  border: theme => `1px solid ${theme.palette.divider}`,
                  overflow: 'hidden',
                  '&:focus-within': {
                    border: theme => `2px solid ${theme.palette.primary.main}`,
                    boxShadow: theme => `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}`,
                  },
                  transition: 'all 0.2s ease',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                    <InputAdornment position="start" sx={{ ml: 1, flexShrink: 0 }}>
                      <Search sx={{ color: theme => theme.palette.primary.main }} />
                    </InputAdornment>
                    <TextField
                      placeholder={isJobsPage ? t('allProducts.search.placeholder.jobs') : t('allProducts.search.placeholder.products')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      variant="standard"
                      sx={{
                        flex: 1,
                        minWidth: 0,
                      }}
                      InputProps={{
                        disableUnderline: true,
                        sx: {
                          '& input': {
                            padding: '12px 8px',
                            fontSize: '1rem',
                            width: '100%',
                          }
                        }
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleApplyFilters();
                        }
                      }}
                    />
                  </Box>
                  <Button
                    variant="contained"
                    onClick={handleApplyFilters}
                    sx={{
                      background: theme => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                      borderRadius: 0,
                      px: 3,
                      py: 1.5,
                      minWidth: 'auto',
                      textTransform: 'none',
                      fontWeight: 'bold',
                      boxShadow: 'none',
                      height: '100%',
                      flexShrink: 0,
                      '&:hover': {
                        background: theme => theme.palette.primary.dark,
                        boxShadow: 'none',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {t('allProducts.apply')}
                  </Button>
                </Box>
                
                {/* Removed legacy category dropdown */}

                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>{t('allProducts.filters.sortBy')}</InputLabel>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    label={t('allProducts.filters.sortBy')}
                  >
                    {sortOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {getSortLabel(option.value)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Kategorien selector trigger */}
                <Button
                  variant="outlined"
                  onClick={() => setCategoryPickerOpen(true)}
                  sx={{
                    height: '56px',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  {tx('header.categories', 'Categories')}
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<FilterList />}
                  onClick={() => setFilterDrawerOpen(true)}
                  sx={{ 
                    minWidth: 120,
                    height: '56px',
                    borderRadius: 2,
                  }}
                >
                  {t('allProducts.moreFilters')}
                </Button>
              </Box>
            )}

          </Box>
        </Container>
      </Box>

      {/* Kategorien Dialog */}
      {categoryPickerOpen && (
        <Dialog
          open
          onClose={() => setCategoryPickerOpen(false)}
          fullScreen={isMobile}
          maxWidth="md"
          PaperProps={{
            sx: {
              borderRadius: isMobile ? 0 : 2,
              width: isMobile ? '100vw' : { xs: '92vw', sm: 680, md: 860 },
              mx: 'auto',
              maxHeight: isMobile ? '100vh' : '80vh',
              overflow: 'hidden',
            },
          }}
        >
          <AppBar sx={{ position: 'sticky', boxShadow: 'none', borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', color: 'text.primary' }}>
            <Toolbar sx={{ minHeight: 48 }}>
              <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
                {t('header.categories')}
              </Typography>
              <IconButton edge="end" onClick={() => setCategoryPickerOpen(false)} aria-label="close">
                <CloseIcon />
              </IconButton>
            </Toolbar>
          </AppBar>
          <Box sx={{ p: 1, overflowY: 'auto', maxHeight: isMobile ? 'calc(100vh - 48px)' : 'calc(80vh - 48px)' }}>
            {enabledTopCategories.map((cat) => {
                const catData = (defaultMegaMenuData as any)[cat];
                if (!catData) return null;
                return (
                  <Accordion key={cat} disableGutters elevation={0} sx={{ '&::before': { display: 'none' }, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ py: 0.75 }}>
                      {categoryIcons[cat] && (
                        <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                          {categoryIcons[cat]}
                        </Box>
                      )}
                      <Typography sx={{ fontWeight: 700 }}>{cat}</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      {(catData.left || [])
                        .filter((item: any) => {
                          const label = (item?.label || '').toString().toLowerCase();
                          return item?.key !== 'all' && label !== 'alle anzeigen';
                        })
                        .map((item: any) => {
                          const cols = (catData.rightByKey?.[item.key]) || [];
                          const hasChildren = cols.some((col: any) => (col.items || []).some((sub: any) => {
                            const lbl = typeof sub === 'string' ? sub : sub?.label ?? '';
                            return !!lbl && lbl.toLowerCase() !== 'alle anzeigen';
                          }));
                          if (!hasChildren) {
                            // Leaf subcategory: select directly (Main / Sub)
                            const entry = `${cat} / ${item.label}`;
                            const entryN = normEntry(entry);
                            const selected = (appliedFilters.megaCategories || []).map(normEntry).includes(entryN);
                            return (
                              <ListItemButton
                                key={`${cat}-${item.key}-leaf`}
                                sx={{
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  mb: 1,
                                  py: 1,
                                  backgroundColor: selected ? 'rgba(2,132,199,0.08)' : 'transparent',
                                }}
                                onClick={() => {
                                  setAppliedFilters(prev => {
                                    const curr = prev.megaCategories || [];
                                    const currN = curr.map(normEntry);
                                    if (currN.includes(entryN)) {
                                      const next = curr.filter((x: string) => normEntry(x) !== entryN);
                                      return { ...prev, megaCategories: next };
                                    }
                                    return { ...prev, megaCategories: Array.from(new Set([...curr, entryN])) };
                                  });
                                }}
                              >
                                <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 600 }} />
                                {selected && (
                                  <ListItemIcon sx={{ minWidth: 32, color: 'primary.main' }}>
                                    <CheckCircleIcon fontSize="small" />
                                  </ListItemIcon>
                                )}
                              </ListItemButton>
                            );
                          }
                          return (
                            <Accordion key={`${cat}-${item.key}`} disableGutters elevation={0} sx={{ '&::before': { display: 'none' }, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ py: 0.5 }}>
                                {item.icon && (
                                  <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                                    {item.icon}
                                  </Box>
                                )}
                                <Typography sx={{ fontWeight: 600 }}>{item.label}</Typography>
                              </AccordionSummary>
                              <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                                {cols
                                  .filter((col: any) => {
                                    const t = (col?.title || '').toString().trim().toLowerCase();
                                    return t !== 'alle anzeigen';
                                  })
                                  .map((col: any) => (
                                  <Box key={`${cat}-${item.key}-${col.title}`} sx={{ mb: 1.25 }}>
                                    {col?.title && (
                                      <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 700, mb: 0.5 }}>
                                        {col.title}
                                      </Typography>
                                    )}
                                    <List disablePadding>
                                      {(col.items || []).map((sub: any) => {
                                        const label = typeof sub === 'string' ? sub : sub?.label ?? '';
                                        if (!label || label.toLowerCase() === 'alle anzeigen') return null;
                                        const entry = `${cat} / ${item.label} / ${label}`;
                                        const entryN = normEntry(entry);
                                        const selected = (appliedFilters.megaCategories || []).map(normEntry).includes(entryN);
                                        return (
                                          <ListItemButton
                                            key={`${cat}-${item.key}-${col.title}-${label}`}
                                            sx={{
                                              borderRadius: 1,
                                              py: 0.75,
                                              backgroundColor: selected ? 'rgba(2,132,199,0.08)' : 'transparent',
                                              border: selected ? '1px solid rgba(2,132,199,0.25)' : '1px solid transparent',
                                              '&:hover': {
                                                backgroundColor: selected ? 'rgba(2,132,199,0.12)' : 'rgba(0,0,0,0.03)'
                                              }
                                            }}
                                            onClick={() => {
                                              setAppliedFilters(prev => {
                                                const curr = prev.megaCategories || [];
                                                const currN = curr.map(normEntry);
                                                if (currN.includes(entryN)) {
                                                  // remove by normalized match
                                                  const next = curr.filter((x: string) => normEntry(x) !== entryN);
                                                  return { ...prev, megaCategories: next };
                                                }
                                                return { ...prev, megaCategories: Array.from(new Set([...curr, entryN])) };
                                              });
                                            }}
                                          >
                                            <ListItemText primary={label} primaryTypographyProps={{ fontWeight: selected ? 700 : 500 }} />
                                            {selected && (
                                              <ListItemIcon sx={{ minWidth: 32, color: 'primary.main' }}>
                                                <CheckCircleIcon fontSize="small" />
                                              </ListItemIcon>
                                            )}
                                          </ListItemButton>
                                        );
                                      })}
                                    </List>
                                  </Box>
                                ))}
                              </AccordionDetails>
                            </Accordion>
                          );
                        })}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
          </Box>
          <DialogActions sx={{ px: 2, py: 1.5 }}>
            <Button
              onClick={() => {
                setAppliedFilters(prev => ({ ...prev, megaCategories: [] }));
              }}
              color="inherit"
              sx={{ textTransform: 'none' }}
            >
              {tx('common.clear', 'Clear')}
            </Button>
            <Button onClick={() => setCategoryPickerOpen(false)} variant="contained" sx={{ textTransform: 'none' }}>
              {tx('common.done', 'Done')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Container
        maxWidth="xl"
        sx={{
          pr: { md: 'var(--right-panel-offset, 0px)' },
          transition: 'padding-right 0.2s ease',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 3,
            mb: 4,
            flexDirection: { xs: 'column', md: (mapDrawerOpen || rightSidePanelOpen) ? 'row' : 'column' },
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              transform: isTablet && mapDrawerOpen && mapExpanded ? 'scale(0.93)' : 'none',
              transformOrigin: 'top left',
              transition: 'transform 0.22s cubic-bezier(0.2, 0.8, 0.2, 1)',
            }}
          >
            {/* Applied Filters */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {appliedFilters.searchQuery && (
                <Chip
                    label={`${t('allProducts.chips.search')}: "${appliedFilters.searchQuery}"`}
                    onDelete={() => {
                      setSearchQuery('');
                      setAppliedFilters(prev => ({ ...prev, searchQuery: '' }));
                      // Remove `search` from URL so it doesn't persist on reload
                      const params = new URLSearchParams(location.search);
                      params.delete('search');
                      navigate({ pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : '' }, { replace: true });
                    }}
                  color="primary"
                  variant="outlined"
                    size="small"
                    sx={{
                      background: theme => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                      color: theme => theme.palette.primary.contrastText,
                      border: 'none',
                      '& .MuiChip-deleteIcon': {
                        color: theme => theme.palette.primary.contrastText,
                      }
                    }}
                />
              )}
              {/* Removed legacy single-category chip in favor of multi-select chips below */}
              {/* MegaMenu category chips */}
              {(appliedFilters.megaCategories && appliedFilters.megaCategories.length > 0) || urlCategoryChip || fallbackUrlChip ? (
                <>
                  {/* Show chip(s) from state; if none, fall back to URL-derived chip */}
                  {(
                    appliedFilters.megaCategories && appliedFilters.megaCategories.length > 0
                      ? appliedFilters.megaCategories
                      : [urlCategoryChip || fallbackUrlChip!]
                  ).map((entry, index) => (
                    <Chip
                      key={`mega-${index}`}
                      label={`${t('allProducts.chips.category')}: ${entry}`}
                      onDelete={() => {
                        if (appliedFilters.megaCategories && appliedFilters.megaCategories.length > 0) {
                          setAppliedFilters(prev => ({
                            ...prev,
                            megaCategories: (prev.megaCategories || []).filter((x: string) => x !== entry)
                          }));
                        } else {
                          setUrlCategoryChip(null);
                          // also clear fallback by normalizing URL
                          const params = new URLSearchParams(location.search);
                          params.delete('category');
                          params.delete('sub');
                          params.delete('mega');
                          navigate({ pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : '' }, { replace: true });
                        }
                      }}
                      color="primary"
                      variant="outlined"
                      size="small"
                      sx={{
                        background: theme => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                        color: theme => theme.palette.primary.contrastText,
                        border: 'none',
                        fontWeight: 'bold',
                        '& .MuiChip-deleteIcon': {
                          color: theme => theme.palette.primary.contrastText,
                          '&:hover': { color: theme => alpha(theme.palette.primary.contrastText, 0.85) }
                        },
                        '&:hover': {
                          background: theme => theme.palette.primary.dark,
                          transform: 'translateY(-1px)',
                          boxShadow: theme => `0 4px 12px ${alpha(theme.palette.primary.main, 0.28)}`
                        },
                        transition: 'all 0.2s ease'
                      }}
                    />
                  ))}
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => { setAppliedFilters(prev => ({ ...prev, megaCategories: [] })); setUrlCategoryChip(null); }}
                    sx={{
                      borderColor: theme => alpha(theme.palette.primary.main, 0.5),
                      color: theme => theme.palette.primary.main,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      px: 2,
                      py: 0.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      background: theme => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
                      '&:hover': {
                        borderColor: theme => theme.palette.primary.main,
                        backgroundColor: theme => alpha(theme.palette.primary.main, 0.12),
                        transform: 'translateY(-1px)',
                        boxShadow: theme => `0 4px 12px ${alpha(theme.palette.primary.main, 0.16)}`
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {tx('common.clear', 'Clear')}
                  </Button>
                </>
              ) : null}
                {appliedFilters.selectedSeller && (
                <Chip
                    label={`${t('allProducts.chips.seller')}: ${appliedFilters.selectedSeller.alias}`}
                    onDelete={() => {
                      setSelectedSeller(null);
                      setSellerSearchQuery('');
                      setAppliedFilters(prev => ({ ...prev, selectedSeller: null }));
                    }}
                  color="primary"
                  variant="outlined"
                    size="small"
                  />
                )}
                {appliedFilters.selectedBrands.size > 0 && (
                  <Chip
                    label={`${t('allProducts.chips.brands')}: ${appliedFilters.selectedBrands.size}`}
                    onDelete={() => {
                      setSelectedBrands(new Set());
                      setAppliedFilters(prev => ({ ...prev, selectedBrands: new Set() }));
                    }}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                )}
                {appliedFilters.showOnlyInStock && (
                <Chip
                  label={t('allProducts.chips.inStockOnly')}
                    onDelete={() => {
                      setShowOnlyInStock(false);
                      setAppliedFilters(prev => ({ ...prev, showOnlyInStock: false }));
                    }}
                    color="primary"
                  variant="outlined"
                    size="small"
                />
              )}
                {appliedFilters.showOnlyOnSale && (
                <Chip
                  label={t('allProducts.chips.onSaleOnly')}
                    onDelete={() => {
                      setShowOnlyOnSale(false);
                      setAppliedFilters(prev => ({ ...prev, showOnlyOnSale: false }));
                    }}
                    color="primary"
                  variant="outlined"
                    size="small"
                  />
                )}
                {appliedFilters.minRating > 0 && (
                  <Chip
                    label={`${t('allProducts.chips.rating')}: ${appliedFilters.minRating}+ ${t('allProducts.chips.ratingSuffix')}`}
                    onDelete={() => {
                      setMinRating(0);
                      setAppliedFilters(prev => ({ ...prev, minRating: 0 }));
                    }}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                )}
                {appliedFilters.selectedCondition !== 'all' && (
                  <Chip
                    label={`${t('allProducts.chips.condition')}: ${appliedFilters.selectedCondition}`}
                    onDelete={() => {
                      setSelectedCondition('all');
                      setAppliedFilters(prev => ({ ...prev, selectedCondition: 'all' }));
                    }}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                )}
                {appliedFilters.requiredSkills.map((skill, index) => (
                  <Chip
                    key={`skill-${index}`}
                    label={`${t('allProducts.chips.skills')}: ${skill}`}
                    onDelete={() => {
                      const newSkills = appliedFilters.requiredSkills.filter((_, i) => i !== index);
                      setRequiredSkills(newSkills);
                      setAppliedFilters(prev => ({ ...prev, requiredSkills: newSkills }));
                    }}
                    color="primary"
                    variant="outlined"
                    size="small"
                    sx={{
                      background: theme => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                      color: theme => theme.palette.primary.contrastText,
                      border: 'none',
                      fontWeight: 'bold',
                      '& .MuiChip-deleteIcon': {
                        color: theme => theme.palette.primary.contrastText,
                        '&:hover': {
                          color: theme => alpha(theme.palette.primary.contrastText, 0.85),
                        }
                      },
                      '&:hover': {
                        background: theme => theme.palette.primary.dark,
                        transform: 'translateY(-1px)',
                        boxShadow: theme => `0 4px 12px ${alpha(theme.palette.primary.main, 0.28)}`,
                      },
                      transition: 'all 0.2s ease',
                    }}
                  />
                ))}
                {appliedFilters.selectedTags && appliedFilters.selectedTags.length > 0 && (
                  <>
                    {appliedFilters.selectedTags.map((tag, index) => (
                      <Chip
                        key={`tag-${index}`}
                        label={`${t('allProducts.chips.tag')}: ${tag}`}
                        onDelete={() => {
                          const newTags = appliedFilters.selectedTags.filter((_, i) => i !== index);
                          setSelectedTags(newTags);
                          setAppliedFilters(prev => ({ ...prev, selectedTags: newTags }));
                        }}
                        color="primary"
                        variant="outlined"
                        size="small"
                        sx={{
                          background: theme => `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.light} 100%)`,
                          color: theme => theme.palette.secondary.contrastText,
                          border: 'none',
                          fontWeight: 'bold',
                          '& .MuiChip-deleteIcon': {
                            color: theme => theme.palette.secondary.contrastText,
                            '&:hover': {
                              color: theme => alpha(theme.palette.secondary.contrastText, 0.85),
                            }
                          },
                          '&:hover': {
                            background: theme => theme.palette.secondary.dark,
                            transform: 'translateY(-1px)',
                            boxShadow: theme => `0 4px 12px ${alpha(theme.palette.secondary.main, 0.26)}`,
                          },
                          transition: 'all 0.2s ease',
                        }}
                      />
                    ))}
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setSelectedTags([]);
                        setAppliedFilters(prev => ({ ...prev, selectedTags: [] }));
                      }}
                      sx={{
                        borderColor: theme => alpha(theme.palette.error.main, 0.55),
                        color: theme => theme.palette.error.main,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        px: 2,
                        py: 0.5,
                        borderRadius: 2,
                        textTransform: 'none',
                        background: theme => `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.06)} 0%, ${alpha(theme.palette.error.main, 0.02)} 100%)`,
                        border: theme => `1px solid ${alpha(theme.palette.error.main, 0.55)}`,
                        '&:hover': {
                          borderColor: theme => theme.palette.error.main,
                          backgroundColor: theme => alpha(theme.palette.error.main, 0.10),
                          transform: 'translateY(-1px)',
                          boxShadow: theme => `0 4px 12px ${alpha(theme.palette.error.main, 0.20)}`,
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      Clear All Tags
                    </Button>
                  </>
                )}
                {appliedFilters.priceRange[0] > 0 || appliedFilters.priceRange[1] < 2000 ? (
                  <Chip
                    label={`Price: $${appliedFilters.priceRange[0]} - $${appliedFilters.priceRange[1]}`}
                    onDelete={() => {
                      setPriceRange([0, 2000]);
                      setAppliedFilters(prev => ({ ...prev, priceRange: [0, 2000] }));
                    }}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                ) : null}
                {appliedFilters.customFilter1 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: theme => theme.palette.text.secondary, mr: 1 }}>
                      Job Type:
                    </Typography>
                    {appliedFilters.customFilter1.split(',').map((value, index) => (
                      <Chip
                        key={`field1-${index}`}
                        label={value.trim()}
                        onDelete={() => {
                          const values = appliedFilters.customFilter1.split(',').map(v => v.trim());
                          const newValues = values.filter((_, i) => i !== index);
                          const newFilter1 = newValues.join(', ');
                          setCustomFilter1(newFilter1);
                          setAppliedFilters(prev => ({ ...prev, customFilter1: newFilter1 }));
                        }}
                        color="primary"
                        variant="outlined"
                        size="small"
                        sx={{
                          background: theme => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                          color: theme => theme.palette.primary.contrastText,
                          border: 'none',
                          fontWeight: 'bold',
                          '& .MuiChip-deleteIcon': {
                            color: theme => theme.palette.primary.contrastText,
                            '&:hover': {
                              color: theme => alpha(theme.palette.primary.contrastText, 0.85),
                            }
                          },
                          '&:hover': {
                            background: theme => theme.palette.primary.dark,
                            transform: 'translateY(-1px)',
                            boxShadow: theme => `0 4px 12px ${alpha(theme.palette.primary.main, 0.28)}`,
                          },
                          transition: 'all 0.2s ease',
                        }}
                      />
                    ))}
                  </Box>
                )}
                {appliedFilters.customFilter2 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: theme => theme.palette.text.secondary, mr: 1 }}>
                      Experience Level:
                    </Typography>
                    {appliedFilters.customFilter2.split(',').map((value, index) => (
                      <Chip
                        key={`field2-${index}`}
                        label={value.trim()}
                        onDelete={() => {
                          const values = appliedFilters.customFilter2.split(',').map(v => v.trim());
                          const newValues = values.filter((_, i) => i !== index);
                          const newFilter2 = newValues.join(', ');
                          setCustomFilter2(newFilter2);
                          setAppliedFilters(prev => ({ ...prev, customFilter2: newFilter2 }));
                        }}
                        color="primary"
                        variant="outlined"
                        size="small"
                        sx={{
                          background: theme => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                          color: theme => theme.palette.primary.contrastText,
                          border: 'none',
                          fontWeight: 'bold',
                          '& .MuiChip-deleteIcon': {
                            color: theme => theme.palette.primary.contrastText,
                            '&:hover': {
                              color: theme => alpha(theme.palette.primary.contrastText, 0.85),
                            }
                          },
                          '&:hover': {
                            background: theme => theme.palette.primary.dark,
                            transform: 'translateY(-1px)',
                            boxShadow: theme => `0 4px 12px ${alpha(theme.palette.primary.main, 0.28)}`,
                          },
                          transition: 'all 0.2s ease',
                        }}
                      />
                    ))}
                  </Box>
                )}
                {appliedFilters.customFilter3 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: theme => theme.palette.text.secondary, mr: 1 }}>
                      Required Skills:
                    </Typography>
                    {appliedFilters.customFilter3.split(',').map((value, index) => (
                      <Chip
                        key={`field3-${index}`}
                        label={value.trim()}
                        onDelete={() => {
                          const values = appliedFilters.customFilter3.split(',').map(v => v.trim());
                          const newValues = values.filter((_, i) => i !== index);
                          const newFilter3 = newValues.join(', ');
                          setCustomFilter3(newFilter3);
                          setAppliedFilters(prev => ({ ...prev, customFilter3: newFilter3 }));
                        }}
                        color="primary"
                        variant="outlined"
                        size="small"
                        sx={{
                          background: theme => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                          color: theme => theme.palette.primary.contrastText,
                          border: 'none',
                          fontWeight: 'bold',
                          '& .MuiChip-deleteIcon': {
                            color: theme => theme.palette.primary.contrastText,
                            '&:hover': {
                              color: theme => alpha(theme.palette.primary.contrastText, 0.85),
                            }
                          },
                          '&:hover': {
                            background: theme => theme.palette.primary.dark,
                            transform: 'translateY(-1px)',
                            boxShadow: theme => `0 4px 12px ${alpha(theme.palette.primary.main, 0.28)}`,
                          },
                          transition: 'all 0.2s ease',
                        }}
                      />
                    ))}
                  </Box>
                )}
                {appliedFilters.customFilter4 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: theme => theme.palette.text.secondary, mr: 1 }}>
                      Benefits:
                    </Typography>
                    {appliedFilters.customFilter4.split(',').map((value, index) => (
                      <Chip
                        key={`field4-${index}`}
                        label={value.trim()}
                        onDelete={() => {
                          const values = appliedFilters.customFilter4.split(',').map(v => v.trim());
                          const newValues = values.filter((_, i) => i !== index);
                          const newFilter4 = newValues.join(', ');
                          setCustomFilter4(newFilter4);
                          setAppliedFilters(prev => ({ ...prev, customFilter4: newFilter4 }));
                        }}
                        color="primary"
                        variant="outlined"
                        size="small"
                        sx={{
                          background: theme => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                          color: theme => theme.palette.primary.contrastText,
                          border: 'none',
                          fontWeight: 'bold',
                          '& .MuiChip-deleteIcon': {
                            color: theme => theme.palette.primary.contrastText,
                            '&:hover': {
                              color: theme => alpha(theme.palette.primary.contrastText, 0.85),
                            }
                          },
                          '&:hover': {
                            background: theme => theme.palette.primary.dark,
                            transform: 'translateY(-1px)',
                            boxShadow: theme => `0 4px 12px ${alpha(theme.palette.primary.main, 0.28)}`,
                          },
                          transition: 'all 0.2s ease',
                        }}
                      />
                    ))}
                  </Box>
                )}
                {appliedFilters.customFilter5 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: theme => theme.palette.text.secondary, mr: 1 }}>
                      City/Location:
                    </Typography>
                    {appliedFilters.customFilter5.split(',').map((value, index) => (
                      <Chip
                        key={`field5-${index}`}
                        label={value.trim()}
                        onDelete={() => {
                          const values = appliedFilters.customFilter5.split(',').map(v => v.trim());
                          const newValues = values.filter((_, i) => i !== index);
                          const newFilter5 = newValues.join(', ');
                          setCustomFilter5(newFilter5);
                          setAppliedFilters(prev => ({ ...prev, customFilter5: newFilter5 }));
                        }}
                        color="primary"
                        variant="outlined"
                        size="small"
                        sx={{
                          background: theme => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                          color: theme => theme.palette.primary.contrastText,
                          border: 'none',
                          fontWeight: 'bold',
                          '& .MuiChip-deleteIcon': {
                            color: theme => theme.palette.primary.contrastText,
                            '&:hover': {
                              color: theme => alpha(theme.palette.primary.contrastText, 0.85),
                            }
                          },
                          '&:hover': {
                            background: theme => theme.palette.primary.dark,
                            transform: 'translateY(-1px)',
                            boxShadow: theme => `0 4px 12px ${alpha(theme.palette.primary.main, 0.28)}`,
                          },
                          transition: 'all 0.2s ease',
                        }}
                      />
                    ))}
                  </Box>
                )}
            </Box>
      </Box>

            {!(isMobile && mapDrawerOpen) && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="h6" color="text.secondary">
                  {filteredProducts.length} {isJobsPage ? t('allProducts.pagination.jobsFound') : t('allProducts.pagination.productsFound')}
                  {Math.max(1, Math.ceil(filteredProducts.length / itemsPerPage)) > 1 && (
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      ({Math.max(1, Math.ceil(filteredProducts.length / itemsPerPage))} {t('allProducts.pagination.pages')})
                    </Typography>
                  )}
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('allProducts.pagination.itemsPerPage')}
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 80 }}>
                      <Select
                        value={itemsPerPage}
                        onChange={(e) => {
                          const newItemsPerPage = Number(e.target.value);
                          setItemsPerPage(newItemsPerPage);
                          setCurrentPage(1);
                        }}
                        variant="outlined"
                      >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={15}>15</MenuItem>
                        <MenuItem value={20}>20</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  <Tooltip title={t('common.map') || 'Map'}>
                    <IconButton
                      onClick={() => setMapDrawerOpen((v) => !v)}
                      sx={{
                        borderRadius: 2,
                        border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
                        background: theme => mapDrawerOpen
                          ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.18)} 0%, ${alpha(theme.palette.primary.light, 0.26)} 100%)`
                          : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.10)} 0%, ${alpha(theme.palette.primary.light, 0.18)} 100%)`,
                        boxShadow: theme => `0 8px 20px ${alpha(theme.palette.primary.main, 0.14)}`,
                        '&:hover': {
                          background: theme => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.16)} 0%, ${alpha(theme.palette.primary.light, 0.26)} 100%)`,
                          transform: 'translateY(-1px)',
                          boxShadow: theme => `0 12px 26px ${alpha(theme.palette.primary.main, 0.18)}`,
                        },
                        transition: 'all 0.2s ease',
                      }}
                      aria-label="toggle map"
                    >
                      <MapIcon sx={{ color: theme => theme.palette.primary.main }} />
                    </IconButton>
                  </Tooltip>

                  {isJobsPage && (
                    <Tooltip title={tx('allJobs.preview', 'Preview')}>
                      <IconButton
                        onClick={() => setJobInfoPanelOpen(true)}
                        sx={{
                          borderRadius: 2,
                          border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
                          background: theme => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.10)} 0%, ${alpha(theme.palette.primary.light, 0.18)} 100%)`,
                          boxShadow: theme => `0 8px 20px ${alpha(theme.palette.primary.main, 0.14)}`,
                          '&:hover': {
                            background: theme => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.16)} 0%, ${alpha(theme.palette.primary.light, 0.26)} 100%)`,
                            transform: 'translateY(-1px)',
                            boxShadow: theme => `0 12px 26px ${alpha(theme.palette.primary.main, 0.18)}`,
                          },
                          transition: 'all 0.2s ease',
                        }}
                        aria-label="open job info"
                      >
                        <InfoOutlinedIcon sx={{ color: theme => theme.palette.primary.main }} />
                      </IconButton>
                    </Tooltip>
                  )}

                  <Tooltip title={t('allProducts.reels') || 'Reels'}>
                    <IconButton
                      onClick={() => setReelsOpen(true)}
                      sx={{
                        borderRadius: 2,
                        border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
                        background: theme => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.10)} 0%, ${alpha(theme.palette.primary.light, 0.18)} 100%)`,
                        boxShadow: theme => `0 8px 20px ${alpha(theme.palette.primary.main, 0.14)}`,
                        '&:hover': {
                          background: theme => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.16)} 0%, ${alpha(theme.palette.primary.light, 0.26)} 100%)`,
                          transform: 'translateY(-1px)',
                          boxShadow: theme => `0 12px 26px ${alpha(theme.palette.primary.main, 0.18)}`,
                        },
                        transition: 'all 0.2s ease',
                      }}
                      aria-label="open reels"
                    >
                      <VideoLibraryIcon sx={{ color: theme => theme.palette.primary.main }} />
                    </IconButton>
                  </Tooltip>

                  {isJobsPage && (
                    <ToggleButtonGroup
                      value={viewMode}
                      exclusive
                      onChange={(_, newMode) => newMode && setViewMode(newMode)}
                      size="small"
                    >
                      <ToggleButton value="grid">
                        <ViewModule />
                      </ToggleButton>
                      <ToggleButton value="list">
                        <ViewList />
                      </ToggleButton>
                    </ToggleButtonGroup>
                  )}
                </Box>
              </Box>
            )}

            {/* Products Grid/List */}
            {!(isMobile && mapDrawerOpen) && (
              loadingProducts ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                  <CircularProgress size={60} />
                </Box>
              ) : pageLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" color="text.secondary">
                      Loading page {currentPage}...
                    </Typography>
                  </Box>
                </Box>
              ) : productsError ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="h6" color="error" gutterBottom>
                    {productsError}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Using sample data instead
                  </Typography>
                </Box>
              ) : (
                <AnimatePresence mode="wait">
                  <motion.div
                    key={viewMode}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                  {viewMode === 'grid' ? (
                    <Box sx={{ 
                      display: 'grid', 
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: '1fr',
                        md: (mapDrawerOpen || rightSidePanelOpen) ? '1fr' : 'repeat(2, 1fr)',
                        lg: (mapDrawerOpen || rightSidePanelOpen) ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                        xl: (mapDrawerOpen || rightSidePanelOpen) ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)'
                      },
                      gridAutoRows: { xs: 'auto', md: '1fr' }, // Natural height on mobile, equal heights on larger screens
                      gap: mapDrawerOpen ? 0.75 : { xs: 2, sm: 2, md: 1 },
                      alignItems: 'stretch',
                      '& > *': {
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                      }
                    }}>
                      {paginatedProducts.map((product) => (
                        <Box
                          key={product.id}
                          sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            transform: 'none',
                          }}
                        >
                          <ProductCard
                            product={transformProductForCard(product)}
                            onAddToCart={handleAddToCart}
                            onToggleFavorite={handleToggleFavorite}
                            onViewDetails={handleViewDetails}
                            onShare={handleShare}
                            isFavorite={favorites.has(product.id)}
                            isOwnProduct={isAuthenticated && user?.id === product.sellerId}
                            showSeller={false}
                            variant="default"
                            navigationState={{
                              currentPage,
                              filters: {
                                searchQuery,
                                selectedCategory,
                                sortBy,
                                priceRange,
                                selectedCondition,
                                showOnlyInStock,
                                selectedBrands: Array.from(selectedBrands),
                                minRating,
                                viewMode
                              },
                              searchQuery,
                              sortBy,
                              viewMode
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: 0,
                      bgcolor: 'background.paper',
                      borderRadius: 1,
                      border: theme => `1px solid ${theme.palette.divider}`,
                      overflow: 'hidden'
                    }}>
                      {paginatedProducts.map((product) => (
                        <Box key={product.id} sx={{ 
                          borderBottom: theme => `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                          '&:last-child': { borderBottom: 'none' }
                        }}>
                          <ProductCard
                            product={transformProductForCard(product)}
                            onAddToCart={handleAddToCart}
                            onToggleFavorite={handleToggleFavorite}
                            onViewDetails={handleViewDetails}
                            onShare={handleShare}
                            isFavorite={favorites.has(product.id)}
                            isOwnProduct={isAuthenticated && user?.id === product.sellerId}
                            showSeller={false}
                            variant="compact"
                            navigationState={{
                              currentPage,
                              filters: {
                                searchQuery,
                                selectedCategory,
                                sortBy,
                                priceRange,
                                selectedCondition,
                                showOnlyInStock,
                                selectedBrands: Array.from(selectedBrands),
                                minRating,
                                viewMode
                              },
                              searchQuery,
                              sortBy,
                              viewMode
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                  )}
                  </motion.div>
                </AnimatePresence>
              )
            )}

            {/* Enhanced Pagination - Always show when we have products */}
            {filteredProducts.length > 0 && !(isMobile && mapDrawerOpen) && (
              <Box sx={{ mt: 4 }}>
                {/* Pagination Info */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  mb: 2,
                  flexWrap: 'wrap',
                  gap: 2
                }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('common.showing')} {filteredProducts.length === 0 ? 0 : ((currentPage - 1) * itemsPerPage) + 1} {t('common.to')} {Math.min(currentPage * itemsPerPage, filteredProducts.length)} {t('common.of')} {filteredProducts.length} {isJobsPage ? t('allProducts.pagination.jobsFound') : t('allProducts.pagination.productsFound')}
                  </Typography>
                  
                  {/* Page Size Selector */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('allProducts.pagination.itemsPerPage')}
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 80 }}>
                      <Select
                        value={itemsPerPage}
                        onChange={(e) => {
                          const newItemsPerPage = Number(e.target.value);
                          setItemsPerPage(newItemsPerPage);
                          setCurrentPage(1); // Reset to first page when changing page size
                          // The useEffect will trigger fetchProducts with the new page size
                        }}
                        variant="outlined"
                      >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={15}>15</MenuItem>
                        <MenuItem value={20}>20</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>

                {/* Pagination Controls */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: 'wrap'
                }}>
                  {/* First Page Button */}
                  <Button
                    variant="outlined"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1 || Math.ceil(filteredProducts.length / itemsPerPage) <= 1}
                    size="small"
                  >
                    {t('allProducts.pagination.first')}
                  </Button>
                  
                  {/* Previous Page Button */}
                  <Button
                    variant="outlined"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1 || Math.ceil(filteredProducts.length / itemsPerPage) <= 1}
                    size="small"
                  >
                    {t('allProducts.pagination.previous')}
                  </Button>

                  {/* Page Numbers - Always show, even for single page */}
                  <Pagination
                    count={Math.max(1, Math.ceil(filteredProducts.length / itemsPerPage))}
                    page={Math.min(currentPage, Math.max(1, Math.ceil(filteredProducts.length / itemsPerPage)))}
                    onChange={(_, page) => setCurrentPage(page)}
                    color="primary"
                    size="large"
                    showFirstButton={Math.ceil(filteredProducts.length / itemsPerPage) > 1}
                    showLastButton={Math.ceil(filteredProducts.length / itemsPerPage) > 1}
                    siblingCount={Math.ceil(filteredProducts.length / itemsPerPage) > 1 ? 1 : 0}
                    boundaryCount={Math.ceil(filteredProducts.length / itemsPerPage) > 1 ? 1 : 0}
                    hidePrevButton={Math.ceil(filteredProducts.length / itemsPerPage) <= 1}
                    hideNextButton={Math.ceil(filteredProducts.length / itemsPerPage) <= 1}
                  />

                  {/* Next Page Button */}
                  <Button
                    variant="outlined"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === Math.max(1, Math.ceil(filteredProducts.length / itemsPerPage)) || Math.ceil(filteredProducts.length / itemsPerPage) <= 1}
                    size="small"
                  >
                    {t('allProducts.pagination.next')}
                  </Button>
                  
                  {/* Last Page Button */}
                  <Button
                    variant="outlined"
                    onClick={() => setCurrentPage(Math.max(1, Math.ceil(filteredProducts.length / itemsPerPage)))}
                    disabled={currentPage === Math.max(1, Math.ceil(filteredProducts.length / itemsPerPage)) || Math.ceil(filteredProducts.length / itemsPerPage) <= 1}
                    size="small"
                  >
                    {t('allProducts.pagination.last')}
                  </Button>
                </Box>

                {/* Quick Page Jump - Show for single page too, but disable input */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  gap: 1,
                  mt: 2
                }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('allProducts.pagination.goToPage')}
                  </Typography>
                  <TextField
                    size="small"
                    type="number"
                    value={Math.min(currentPage, Math.max(1, Math.ceil(filteredProducts.length / itemsPerPage)))}
                    disabled={Math.ceil(filteredProducts.length / itemsPerPage) <= 1}
                    onChange={(e) => {
                      const pages = Math.max(1, Math.ceil(filteredProducts.length / itemsPerPage));
                      const page = Math.max(1, Math.min(pages, Number(e.target.value)));
                      setCurrentPage(page);
                    }}
                    inputProps={{ 
                      min: 1, 
                      max: Math.max(1, Math.ceil(filteredProducts.length / itemsPerPage)),
                      style: { width: '60px', textAlign: 'center' }
                    }}
                    variant="outlined"
                  />
                </Box>
              </Box>
            )}

            {/* No Results */}
            {filteredProducts.length === 0 && !loadingProducts && (
              <Box sx={{ textAlign: 'center', py: 10 }}>
                <SentimentDissatisfiedIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  No {isJobsPage ? 'jobs' : 'products'} found
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Try adjusting your search criteria or filters
                </Typography>
                <Button variant="contained" onClick={handleClearFilters}>
                  Clear All Filters
                </Button>
              </Box>
            )}
          </Box>

          {mapDrawerOpen && (
            <Box
              sx={{
                width: { xs: '100vw', md: mapExpanded ? 560 : 460, lg: mapExpanded ? 900 : 620 },
                flexShrink: 0,
                position: { xs: 'fixed', md: 'sticky' },
                top: { xs: 0, md: 16 },
                left: { xs: 0, md: 'auto' },
                right: { xs: 0, md: 'auto' },
                bottom: { xs: 0, md: 'auto' },
                zIndex: theme => ({ xs: theme.zIndex.appBar, md: 'auto' }),
                alignSelf: { xs: 'stretch', md: 'flex-start' },
                height: { xs: '100vh', md: 'calc(100vh - 140px)' },
                minHeight: { xs: '100vh', md: 520 },
                overflow: { xs: 'hidden', md: 'visible' },
                transition: theme => theme.transitions.create('width', {
                  duration: 260,
                  easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
                }),
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 980,
                  pointerEvents: 'auto',
                  display: { xs: 'none', md: 'block' },
                }}
              >
                <IconButton
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setMapExpanded((v) => !v);
                  }}
                  sx={{
                    width: 34,
                    height: 64,
                    borderRadius: 999,
                    border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
                    background: theme => `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.92)} 0%, ${alpha(theme.palette.background.paper, 0.76)} 100%)`,
                    boxShadow: theme => `0 16px 36px ${alpha(theme.palette.common.black, 0.18)}`,
                    backdropFilter: 'blur(14px)',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: theme => `0 20px 44px ${alpha(theme.palette.common.black, 0.20)}`,
                      background: theme => `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.96)} 0%, ${alpha(theme.palette.background.paper, 0.82)} 100%)`,
                    },
                    transition: 'all 0.18s ease',
                  }}
                  aria-label={mapExpanded ? 'collapse map' : 'expand map'}
                >
                  {mapExpanded ? <ChevronRight /> : <ChevronLeft />}
                </IconButton>
              </Box>

              <Box
                sx={{
                  height: '100%',
                  borderRadius: { xs: 0, md: 3 },
                  overflow: 'hidden',
                  border: theme => ({ xs: 'none', md: `1px solid ${theme.palette.divider}` }),
                  boxShadow: theme => ({ xs: 'none', md: `0 16px 44px ${alpha(theme.palette.common.black, 0.12)}` }),
                  bgcolor: 'background.paper',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={{ flex: 1, p: { xs: 0, md: 1.5 }, bgcolor: theme => ({ xs: theme.palette.background.paper, md: alpha(theme.palette.grey[50], 0.9) }) }}>
                  <Box
                    sx={{
                      height: '100%',
                      borderRadius: { xs: 0, md: 2.5 },
                      overflow: 'hidden',
                      border: theme => ({ xs: 'none', md: `1px solid ${alpha(theme.palette.divider, 0.9)}` }),
                      position: 'relative',
                    }}
                    ref={mapClipRef}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        zIndex: 900,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: 1,
                      }}
                    >
                      {isMobile && (
                        <>
                          {mapDrawerOpen && (
                            <Tooltip title={tx('common.close', 'Close')}>
                              <IconButton
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setMapDrawerOpen(false);

                                  try {
                                    const params = new URLSearchParams(location.search);
                                    params.delete('map');
                                    params.delete('lat');
                                    params.delete('lng');
                                    params.delete('zoom');
                                    params.delete('place');
                                    const pathname = latestPathnameRef.current || location.pathname;
                                    navigate(
                                      { pathname, search: params.toString() ? `?${params.toString()}` : '' },
                                      { replace: true }
                                    );
                                  } catch {
                                    // ignore
                                  }
                                }}
                                sx={{
                                  width: 48,
                                  height: 48,
                                  borderRadius: 999,
                                  border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
                                  bgcolor: theme => alpha(theme.palette.background.paper, 0.88),
                                  boxShadow: theme => `0 12px 28px ${alpha(theme.palette.common.black, 0.14)}`,
                                  backdropFilter: 'blur(10px)',
                                  '&:hover': {
                                    transform: 'translateY(-1px)',
                                    boxShadow: theme => `0 16px 34px ${alpha(theme.palette.common.black, 0.16)}`,
                                    bgcolor: theme => alpha(theme.palette.background.paper, 0.94),
                                  },
                                  transition: 'all 0.18s ease',
                                }}
                                aria-label="close map"
                              >
                                <CloseIcon fontSize="medium" />
                              </IconButton>
                            </Tooltip>
                          )}

                          <Tooltip title={tx('allProducts.map.list', 'List')}>
                            <IconButton
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setMobileMapListAnchorEl(e.currentTarget);
                              }}
                              sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 999,
                                border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
                                bgcolor: theme => alpha(theme.palette.background.paper, 0.88),
                                boxShadow: theme => `0 12px 28px ${alpha(theme.palette.common.black, 0.14)}`,
                                backdropFilter: 'blur(10px)',
                                '&:hover': {
                                  transform: 'translateY(-1px)',
                                  boxShadow: theme => `0 16px 34px ${alpha(theme.palette.common.black, 0.16)}`,
                                  bgcolor: theme => alpha(theme.palette.background.paper, 0.94),
                                },
                                transition: 'all 0.18s ease',
                              }}
                              aria-label="open list"
                            >
                              <ViewList fontSize="medium" />
                            </IconButton>
                          </Tooltip>

                          <Menu
                            anchorEl={mobileMapListAnchorEl}
                            open={Boolean(mobileMapListAnchorEl)}
                            onClose={() => setMobileMapListAnchorEl(null)}
                            PaperProps={{
                              sx: {
                                mt: 1,
                                borderRadius: 3,
                                overflow: 'hidden',
                                width: 'min(92vw, 420px)',
                                height: 'min(70vh, 560px)',
                                backgroundColor: theme => alpha(theme.palette.background.paper, 0.92),
                                backdropFilter: 'blur(14px)',
                                border: theme => `1px solid ${alpha(theme.palette.common.white, 0.55)}`,
                                outline: 'none',
                                boxShadow: theme => `0 22px 54px ${alpha(theme.palette.common.black, 0.22)}`,
                                display: 'flex',
                                flexDirection: 'column',
                              }
                            }}
                            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            MenuListProps={{
                              sx: {
                                p: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1,
                                minHeight: 0,
                                overflow: 'hidden',
                              },
                              onClick: (e: any) => {
                                // Prevent map click handlers from closing cards unexpectedly
                                try {
                                  e.stopPropagation();
                                } catch {
                                  // ignore
                                }
                              },
                            }}
                          >
                            <Box
                              sx={{
                                px: 1.25,
                                py: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                borderBottom: theme => `1px solid ${alpha(theme.palette.common.white, 0.42)}`,
                                flex: '0 0 auto',
                              }}
                            >
                              <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                                {tx('allProducts.map.listTitle', 'Listings')}
                              </Typography>
                              <IconButton
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setMobileMapListAnchorEl(null);
                                }}
                                size="small"
                                sx={{ width: 34, height: 34 }}
                                aria-label="close list"
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>

                            <Box
                              sx={{
                                p: 1,
                                flex: 1,
                                minHeight: 0,
                                overflowY: 'auto',
                                WebkitOverflowScrolling: 'touch',
                                overscrollBehavior: 'contain',
                              }}
                            >
                              {(paginatedProducts || []).length === 0 ? (
                                <Box sx={{ py: 4, textAlign: 'center' }}>
                                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                                    {tx('allProducts.map.listEmpty', 'No items to show')}
                                  </Typography>
                                </Box>
                              ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  {paginatedProducts.map((product) => (
                                    <Box
                                      key={product.id}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setMobileMapListAnchorEl(null);
                                        handleViewDetails(product.id);
                                      }}
                                      sx={{
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        border: theme => `1px solid ${alpha(theme.palette.common.white, 0.40)}`,
                                        background: theme => `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.88)} 0%, ${alpha(theme.palette.background.paper, 0.70)} 100%)`,
                                        boxShadow: theme => `0 12px 28px ${alpha(theme.palette.common.black, 0.12)}`,
                                        '&:active': {
                                          transform: 'scale(0.995)',
                                        },
                                        transition: 'transform 0.12s ease',
                                      }}
                                    >
                                      <ProductCard
                                        product={transformProductForCard(product)}
                                        onAddToCart={handleAddToCart}
                                        onToggleFavorite={handleToggleFavorite}
                                        onViewDetails={handleViewDetails}
                                        onShare={handleShare}
                                        isFavorite={favorites.has(product.id)}
                                        isOwnProduct={isAuthenticated && user?.id === product.sellerId}
                                        showSeller={false}
                                        variant="compact"
                                        navigationState={{
                                          currentPage,
                                          filters: {
                                            searchQuery,
                                            selectedCategory,
                                            sortBy,
                                            priceRange,
                                            selectedCondition,
                                            showOnlyInStock,
                                            selectedBrands: Array.from(selectedBrands),
                                            minRating,
                                            viewMode
                                          },
                                          searchQuery,
                                          sortBy,
                                          viewMode
                                        }}
                                      />
                                    </Box>
                                  ))}
                                </Box>
                              )}
                            </Box>
                          </Menu>
                        </>
                      )}

                      {isMobile && (
                        <>
                          <Tooltip title={tx('allProducts.map.filters', 'Filters')}>
                            <IconButton
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setFilterDrawerOpen(true);
                              }}
                              sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 999,
                                border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
                                bgcolor: theme => alpha(theme.palette.background.paper, 0.88),
                                boxShadow: theme => `0 12px 28px ${alpha(theme.palette.common.black, 0.14)}`,
                                backdropFilter: 'blur(10px)',
                                '&:hover': {
                                  transform: 'translateY(-1px)',
                                  boxShadow: theme => `0 16px 34px ${alpha(theme.palette.common.black, 0.16)}`,
                                  bgcolor: theme => alpha(theme.palette.background.paper, 0.94),
                                },
                                transition: 'all 0.18s ease',
                              }}
                              aria-label="open filters"
                            >
                              <FilterList fontSize="medium" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title={tx('header.categories', 'Categories')}>
                            <IconButton
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setCategoryPickerOpen(true);
                              }}
                              sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 999,
                                border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
                                bgcolor: theme => alpha(theme.palette.background.paper, 0.88),
                                boxShadow: theme => `0 12px 28px ${alpha(theme.palette.common.black, 0.14)}`,
                                backdropFilter: 'blur(10px)',
                                '&:hover': {
                                  transform: 'translateY(-1px)',
                                  boxShadow: theme => `0 16px 34px ${alpha(theme.palette.common.black, 0.16)}`,
                                  bgcolor: theme => alpha(theme.palette.background.paper, 0.94),
                                },
                                transition: 'all 0.18s ease',
                              }}
                              aria-label="open categories"
                            >
                              <MenuBookIcon fontSize="medium" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}

                      <Tooltip title={t('common.myLocation') || 'My location'}>
                        <IconButton
                          onClick={() => {
                            if (!navigator.geolocation) return;
                            navigator.geolocation.getCurrentPosition(
                              (pos) => {
                                setMapCenter([pos.coords.latitude, pos.coords.longitude]);
                              },
                              () => {}
                            );
                          }}
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 999,
                            border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
                            bgcolor: theme => alpha(theme.palette.background.paper, 0.88),
                            boxShadow: theme => `0 12px 28px ${alpha(theme.palette.common.black, 0.14)}`,
                            backdropFilter: 'blur(10px)',
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              boxShadow: theme => `0 16px 34px ${alpha(theme.palette.common.black, 0.16)}`,
                              bgcolor: theme => alpha(theme.palette.background.paper, 0.94),
                            },
                            transition: 'all 0.18s ease',
                          }}
                          aria-label="my location"
                        >
                          <MyLocationIcon fontSize="medium" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <Box
                      sx={{
                        position: 'absolute',
                        top: 10,
                        left: 56,
                        zIndex: 900,
                        pointerEvents: 'none',
                      }}
                    >
                      <motion.div
                        layout
                        transition={{ duration: 0.18, ease: 'easeOut' }}
                        style={{ pointerEvents: 'auto' }}
                      >
                        <Box
                          sx={{
                            height: 48,
                            width: mapPlaceSearchOpen ? 360 : 48,
                            maxWidth: mapPlaceSearchOpen ? 'calc(100vw - 130px)' : 48,
                            borderRadius: 999,
                            border: theme => `1px solid ${alpha(theme.palette.primary.main, mapPlaceSearchOpen ? 0.14 : 0.22)}`,
                            background: theme => alpha(theme.palette.background.paper, 0.88),
                            boxShadow: theme => `0 12px 28px ${alpha(theme.palette.common.black, 0.14)}`,
                            backdropFilter: 'blur(12px)',
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            transition: 'width 0.18s ease, max-width 0.18s ease',
                          }}
                        >
                          {!mapPlaceSearchOpen ? (
                            <Tooltip title={tx('allProducts.map.searchPlace', 'Search a place')}>
                              <IconButton
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setMapPlaceSearchOpen(true);
                                }}
                                sx={{
                                  width: 48,
                                  height: 48,
                                  borderRadius: 999,
                                  pointerEvents: 'auto',
                                  '&:hover': {
                                    transform: 'translateY(-1px)',
                                    backgroundColor: theme => alpha(theme.palette.background.paper, 0.12),
                                  },
                                  transition: 'all 0.18s ease',
                                }}
                                aria-label="search place"
                              >
                                <Search fontSize="medium" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <TextField
                              fullWidth
                              value={mapPlaceQuery}
                              onChange={(e) => setMapPlaceQuery(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setMapPlaceSearchOpen(false);
                                  return;
                                }
                                if (e.key !== 'Enter') return;
                                e.preventDefault();
                                e.stopPropagation();
                                const q = (mapPlaceQuery || '').trim();
                                if (!q) return;

                                setMapPlaceSearching(true);
                                setMapDrawerOpen(true);
                                didAutoLocateRef.current = true;
                                pendingPlaceCenterRef.current = null;

                                const params = new URLSearchParams(latestSearchRef.current || '');
                                params.set('map', '1');
                                params.set('place', q);
                                params.delete('lat');
                                params.delete('lng');
                                params.delete('zoom');
                                const pathname = latestPathnameRef.current || location.pathname;
                                navigate({ pathname, search: `?${params.toString()}` }, { replace: true });
                              }}
                              placeholder={tx('allProducts.map.searchPlaceholder', 'Search a place…')}
                              inputRef={(el) => {
                                mapPlaceInputRef.current = el;
                              }}
                              size="small"
                              variant="standard"
                              sx={{ flex: 1 }}
                              InputProps={{
                                disableUnderline: true,
                                sx: {
                                  height: 48,
                                  px: 0.25,
                                  fontWeight: 800,
                                  display: 'flex',
                                  alignItems: 'center',
                                  '& .MuiInputBase-input': {
                                    boxSizing: 'border-box',
                                    fontSize: '1.05rem',
                                  },
                                },
                                inputProps: {
                                  style: {
                                    height: '48px',
                                    lineHeight: '48px',
                                    padding: '0px',
                                    margin: '0px',
                                  },
                                },
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <IconButton
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const q = (mapPlaceQuery || '').trim();
                                        if (!q) return;

                                        setMapPlaceSearching(true);
                                        setMapDrawerOpen(true);
                                        didAutoLocateRef.current = true;
                                        pendingPlaceCenterRef.current = null;

                                        const params = new URLSearchParams(latestSearchRef.current || '');
                                        params.set('map', '1');
                                        params.set('place', q);
                                        params.delete('lat');
                                        params.delete('lng');
                                        params.delete('zoom');
                                        const pathname = latestPathnameRef.current || location.pathname;
                                        navigate({ pathname, search: `?${params.toString()}` }, { replace: true });
                                      }}
                                      size="small"
                                      sx={{
                                        mr: 0.5,
                                        width: 42,
                                        height: 42,
                                      }}
                                      aria-label="search"
                                    >
                                      {mapPlaceSearching ? (
                                        <CircularProgress size={20} thickness={5} />
                                      ) : (
                                        <Search fontSize="medium" />
                                      )}
                                    </IconButton>
                                  </InputAdornment>
                                ),
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setMapPlaceSearching(false);
                                        setMapPlaceSearchOpen(false);
                                      }}
                                      size="small"
                                      sx={{
                                        ml: 0.5,
                                        width: 42,
                                        height: 42,
                                      }}
                                      aria-label="close"
                                    >
                                      <CloseIcon fontSize="medium" />
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                            />
                          )}
                        </Box>
                      </motion.div>
                    </Box>

                    <style>{`
                      .product-map-popup .leaflet-popup-content-wrapper { background: rgba(255,255,255,0.94); border-radius: 16px; box-shadow: 0 18px 46px rgba(0,0,0,0.22); }
                      .product-map-popup .leaflet-popup-content { margin: 0; }
                      .product-map-popup .leaflet-popup-tip { background: rgba(255,255,255,0.94); }
                      .product-map-tooltip.leaflet-tooltip { background: transparent; border: none; box-shadow: none; padding: 0; }
                      .product-map-tooltip.leaflet-tooltip:before { display: none; }

                      .map-edge-indicator-dot {
                        position: absolute;
                        z-index: 1200;
                        pointer-events: none;
                      }

                      .map-edge-indicator-dot-hit {
                        position: absolute;
                        z-index: 1201;
                        pointer-events: none;
                      }

                      .map-edge-indicator-dot__label {
                        position: absolute;
                        left: 50%;
                        bottom: calc(100% + 8px);
                        transform: translateX(-50%);
                        cursor: pointer;
                        pointer-events: auto;
                        padding: 6px 10px;
                        border-radius: 999px;
                        font-size: 12px;
                        line-height: 1;
                        font-weight: 900;
                        letter-spacing: 0.2px;
                        color: rgba(255,255,255,0.96);
                        background: linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.96)} 0%, ${alpha(theme.palette.primary.dark, 0.94)} 100%);
                        box-shadow: 0 14px 34px rgba(0,0,0,0.24);
                        border: 1px solid ${alpha(theme.palette.common.white, 0.22)};
                        backdrop-filter: blur(10px);
                        -webkit-backdrop-filter: blur(10px);
                        text-transform: uppercase;
                        white-space: nowrap;
                      }

                      .map-edge-indicator-dot__label:after {
                        content: '';
                        position: absolute;
                        left: 50%;
                        top: 100%;
                        width: 10px;
                        height: 10px;
                        background: ${alpha(theme.palette.primary.dark, 0.92)};
                        transform: translateX(-50%) rotate(45deg);
                        filter: drop-shadow(0 10px 14px rgba(0,0,0,0.20));
                      }

                      .map-edge-indicator-dot--right .map-edge-indicator-dot__label {
                        left: auto;
                        bottom: auto;
                        top: 50%;
                        right: calc(100% + 10px);
                        transform: translateY(-50%);
                      }

                      .map-edge-indicator-dot--right .map-edge-indicator-dot__label:after {
                        left: 100%;
                        top: 50%;
                        transform: translateY(-50%) rotate(45deg);
                      }

                      .map-edge-indicator-dot--left .map-edge-indicator-dot__label {
                        left: calc(100% + 10px);
                        bottom: auto;
                        top: 50%;
                        transform: translateY(-50%);
                      }

                      .map-edge-indicator-dot--left .map-edge-indicator-dot__label:after {
                        left: auto;
                        right: 100%;
                        top: 50%;
                        transform: translateY(-50%) rotate(45deg);
                      }

                      .map-edge-indicator-dot--top .map-edge-indicator-dot__label {
                        top: calc(100% + 8px);
                        bottom: auto;
                      }

                      .map-edge-indicator-dot--top .map-edge-indicator-dot__label:after {
                        top: auto;
                        bottom: 100%;
                        transform: translateX(-50%) rotate(45deg);
                      }

                      .map-edge-indicator-dot--bottom .map-edge-indicator-dot__label {
                        top: auto;
                        bottom: calc(100% + 8px);
                      }

                      .map-edge-indicator-dot--bottom .map-edge-indicator-dot__label:after {
                        top: 100%;
                        bottom: auto;
                        transform: translateX(-50%) rotate(45deg);
                      }

                      .map-edge-indicator-dot--corner-tl .map-edge-indicator-dot__label {
                        left: calc(100% + 10px);
                        top: calc(100% + 8px);
                        bottom: auto;
                        transform: none;
                      }

                      .map-edge-indicator-dot--corner-tl .map-edge-indicator-dot__label:after {
                        left: auto;
                        right: 100%;
                        top: 0;
                        bottom: auto;
                        transform: translate(50%, -50%) rotate(45deg);
                      }

                      .map-edge-indicator-dot--corner-tr .map-edge-indicator-dot__label {
                        left: auto;
                        right: calc(100% + 10px);
                        top: calc(100% + 8px);
                        bottom: auto;
                        transform: none;
                      }

                      .map-edge-indicator-dot--corner-tr .map-edge-indicator-dot__label:after {
                        left: 100%;
                        right: auto;
                        top: 0;
                        bottom: auto;
                        transform: translate(-50%, -50%) rotate(45deg);
                      }

                      .map-edge-indicator-dot--corner-bl .map-edge-indicator-dot__label {
                        left: calc(100% + 10px);
                        bottom: calc(100% + 8px);
                        top: auto;
                        transform: none;
                      }

                      .map-edge-indicator-dot--corner-bl .map-edge-indicator-dot__label:after {
                        left: auto;
                        right: 100%;
                        top: auto;
                        bottom: 0;
                        transform: translate(50%, 50%) rotate(45deg);
                      }

                      .map-edge-indicator-dot--corner-br .map-edge-indicator-dot__label {
                        left: auto;
                        right: calc(100% + 10px);
                        bottom: calc(100% + 8px);
                        top: auto;
                        transform: none;
                      }

                      .map-edge-indicator-dot--corner-br .map-edge-indicator-dot__label:after {
                        left: 100%;
                        right: auto;
                        top: auto;
                        bottom: 0;
                        transform: translate(-50%, 50%) rotate(45deg);
                      }

                      .map-edge-indicator-dot__outer {
                        width: 18px;
                        height: 18px;
                        border-radius: 999px;
                        pointer-events: none;
                        background: linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(255,255,255,0.82) 100%);
                        border: 1px solid rgba(255,255,255,0.9);
                        box-shadow: 0 16px 34px rgba(0,0,0,0.16);
                        display: grid;
                        place-items: center;
                        backdrop-filter: blur(10px);
                        -webkit-backdrop-filter: blur(10px);
                      }

                      .map-edge-indicator-dot__inner {
                        width: 10px;
                        height: 10px;
                        border-radius: 999px;
                        pointer-events: none;
                        background: linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.98)} 0%, ${alpha(theme.palette.warning.dark, 0.92)} 100%);
                        box-shadow: 0 12px 24px rgba(0,0,0,0.18);
                        position: relative;
                      }

                      .map-edge-indicator-dot__inner:after {
                        content: '';
                        position: absolute;
                        inset: -10px;
                        border-radius: 999px;
                        background: radial-gradient(circle, rgba(255,255,255,0.38) 0%, rgba(255,255,255,0.00) 60%);
                        opacity: 0.9;
                        animation: mapTopDotPulse 1.9s ease-in-out infinite;
                      }

                      @keyframes mapTopDotPulse {
                        0% { transform: scale(0.72); opacity: 0.25; }
                        55% { transform: scale(1.05); opacity: 0.62; }
                        100% { transform: scale(1.22); opacity: 0.00; }
                      }
                    `}</style>

                    {edgeIndicatorPosition && (
                      <>
                        <Box
                          className={
                            (() => {
                              if (!mapSize) return 'map-edge-indicator-dot';
                              const nearRight = edgeIndicatorPosition.x > mapSize.x - 64;
                              const nearLeft = edgeIndicatorPosition.x < 64;
                              const nearTop = edgeIndicatorPosition.y < 64;
                              const nearBottom = edgeIndicatorPosition.y > mapSize.y - 64;

                              if (nearTop && nearLeft) return 'map-edge-indicator-dot map-edge-indicator-dot--corner-tl';
                              if (nearTop && nearRight) return 'map-edge-indicator-dot map-edge-indicator-dot--corner-tr';
                              if (nearBottom && nearLeft) return 'map-edge-indicator-dot map-edge-indicator-dot--corner-bl';
                              if (nearBottom && nearRight) return 'map-edge-indicator-dot map-edge-indicator-dot--corner-br';

                              return (
                                'map-edge-indicator-dot'
                                + (nearRight ? ' map-edge-indicator-dot--right' : '')
                                + (nearLeft ? ' map-edge-indicator-dot--left' : '')
                                + (nearTop ? ' map-edge-indicator-dot--top' : '')
                                + (nearBottom ? ' map-edge-indicator-dot--bottom' : '')
                              );
                            })()
                          }
                          aria-hidden
                          sx={{
                            left: `${edgeIndicatorPosition.x}px`,
                            top: `${edgeIndicatorPosition.y}px`,
                            transform: 'translate(-50%, -50%)',
                          }}
                        >
                          <Box className="map-edge-indicator-dot__outer">
                            <Box className="map-edge-indicator-dot__inner" />
                          </Box>
                        </Box>

                        <Box
                          className={
                            (() => {
                              if (!mapSize) return 'map-edge-indicator-dot-hit';
                              const nearRight = edgeIndicatorPosition.x > mapSize.x - 64;
                              const nearLeft = edgeIndicatorPosition.x < 64;
                              const nearTop = edgeIndicatorPosition.y < 64;
                              const nearBottom = edgeIndicatorPosition.y > mapSize.y - 64;

                              if (nearTop && nearLeft) return 'map-edge-indicator-dot-hit map-edge-indicator-dot--corner-tl';
                              if (nearTop && nearRight) return 'map-edge-indicator-dot-hit map-edge-indicator-dot--corner-tr';
                              if (nearBottom && nearLeft) return 'map-edge-indicator-dot-hit map-edge-indicator-dot--corner-bl';
                              if (nearBottom && nearRight) return 'map-edge-indicator-dot-hit map-edge-indicator-dot--corner-br';

                              return (
                                'map-edge-indicator-dot-hit'
                                + (nearRight ? ' map-edge-indicator-dot--right' : '')
                                + (nearLeft ? ' map-edge-indicator-dot--left' : '')
                                + (nearTop ? ' map-edge-indicator-dot--top' : '')
                                + (nearBottom ? ' map-edge-indicator-dot--bottom' : '')
                              );
                            })()
                          }
                          aria-hidden
                          sx={{
                            left: `${edgeIndicatorPosition.x}px`,
                            top: `${edgeIndicatorPosition.y}px`,
                            transform: 'translate(-50%, -50%)',
                          }}
                        >
                          <Box
                            className="map-edge-indicator-dot__label"
                            role="button"
                            tabIndex={0}
                            onClick={handleEdgeIndicatorClick}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleEdgeIndicatorClick(e as unknown as React.MouseEvent);
                              }
                            }}
                            aria-label="focus marker"
                          >
                            {(() => {
                              const formatPrice = (v: unknown) =>
                                new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(Number(v ?? 0));

                              // 1) If a specific marker card is active, use its price.
                              if (expandedMapCardId && expandedMapCardId !== 'fallback') {
                                const hit = productsWithCoords.find((x) => String(x.p.id) === String(expandedMapCardId));
                                if (hit) {
                                  const pc: any = transformProductForCard(hit.p);
                                  return formatPrice(pc?.price ?? hit.p.price ?? 0);
                                }
                              }

                              // 2) If we're in fallback mode (no coords), use the first loaded product.
                              if (expandedMapCardId === 'fallback') {
                                const p0 = (products || [])[0];
                                if (p0) {
                                  const pc: any = transformProductForCard(p0);
                                  return formatPrice(pc?.price ?? p0.price ?? 0);
                                }
                              }

                              // 3) Default: use the first product with coordinates, else the first loaded product.
                              const first = productsWithCoords[0];
                              if (first) {
                                const pc: any = transformProductForCard(first.p);
                                return formatPrice(pc?.price ?? first.p.price ?? 0);
                              }

                              const p0 = (products || [])[0];
                              if (p0) {
                                const pc: any = transformProductForCard(p0);
                                return formatPrice(pc?.price ?? p0.price ?? 0);
                              }

                              return '—';
                            })()}
                          </Box>
                        </Box>
                      </>
                    )}

                    <MapContainer
                      center={mapCenter}
                      zoom={mapZoom}
                      style={{ height: '100%', width: '100%' }}
                      scrollWheelZoom
                      zoomSnap={1}
                      zoomDelta={1}
                    >
                      <TileLayer
                        attribution={mapTileProvider.attribution}
                        url={mapTileProvider.url}
                      />
                      <MapCaptureInstance
                        onMapReady={handleMapReady}
                      />
                      <MapBoundsTracker onViewportChange={handleMapViewportChange} />

                      {productsWithCoords.length === 0 && (
                        <Marker position={mapCenter} icon={mapMarkerIcon}>
                          <LeafletTooltip
                            className="product-map-tooltip"
                            permanent
                            interactive
                            direction="top"
                            offset={[0, -28]}
                            opacity={1}
                          >
                            {(() => {
                              const p0 = (products || [])[0];
                              if (!p0) {
                                return (
                                  <Box
                                    sx={{
                                      width: 260,
                                      borderRadius: 3,
                                      overflow: 'hidden',
                                      border: theme => `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                                      background: theme => alpha(theme.palette.background.paper, 0.96),
                                      boxShadow: theme => `0 18px 46px ${alpha(theme.palette.common.black, 0.22)}`,
                                      backdropFilter: 'blur(12px)',
                                    }}
                                  >
                                    <Box sx={{ px: 1.25, py: 1.1 }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                                        {tx('allProducts.map.noProductLocations', 'No product locations available')}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                        {tx('allProducts.map.tipAddLocations', 'Add latitude/longitude to products to show marker cards.')}
                                      </Typography>
                                    </Box>
                                  </Box>
                                );
                              }

                              const pc: any = transformProductForCard(p0);
                              const img = String(pc?.image || p0.image || '/placeholder-product.jpg');
                              const title = String(pc?.name || p0.name || tx('common.untitled', 'Untitled'));
                              const isAuction = Boolean(pc?.auctionId || pc?.auction?.id || (p0 as any)?.auction_id);
                              const auctionEnd = String(pc?.auction?.end_time || (pc?.auction as any)?.endTime || (p0 as any)?.auctions?.end_time || '').trim();
                              const countdown = isAuction ? formatAuctionCountdown(auctionEnd) : '';
                              const auctionBid = pc?.auction?.current_bid;
                              const displayPriceValue = isAuction && typeof auctionBid === 'number' && !isNaN(auctionBid)
                                ? auctionBid
                                : Number(pc?.price ?? p0.price ?? 0);
                              const categories: string[] = Array.isArray(pc?.categories)
                                ? pc.categories
                                : (pc?.category ? [String(pc.category)] : []);
                              const tags: string[] = Array.isArray(pc?.tags) ? pc.tags : [];
                              const rating = Number(pc?.rating ?? 0);
                              const reviewCount = Number(pc?.reviewCount ?? 0);

                              const isExpanded = expandedMapCardId === 'fallback';

                              return (
                                <Box
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (!isExpanded) {
                                      setExpandedMapCardId('fallback');
                                      return;
                                    }
                                    handleViewDetails(p0.id);
                                  }}
                                  sx={{
                                    width: isExpanded ? 300 : 220,
                                    borderRadius: 3,
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    border: theme => `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                                    background: theme => alpha(theme.palette.background.paper, 0.96),
                                    boxShadow: theme => `0 18px 46px ${alpha(theme.palette.common.black, 0.22)}`,
                                    backdropFilter: 'blur(12px)',
                                    '&:hover': {
                                      transform: 'translateY(-1px)',
                                      boxShadow: theme => `0 22px 54px ${alpha(theme.palette.common.black, 0.24)}`,
                                    },
                                    transition: 'all 0.18s ease',
                                    position: 'relative',
                                  }}
                                  role="button"
                                  aria-label="open product"
                                >
                                  {isExpanded && (
                                    <IconButton
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setExpandedMapCardId(null);
                                      }}
                                      sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        zIndex: 2,
                                        width: 30,
                                        height: 30,
                                        bgcolor: theme => alpha(theme.palette.common.white, 0.86),
                                        border: theme => `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                                        '&:hover': { bgcolor: theme => alpha(theme.palette.common.white, 0.94) },
                                      }}
                                      aria-label="close"
                                    >
                                      <CloseIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  )}

                                  <Box
                                    sx={{
                                      height: isExpanded ? 175 : 120,
                                      backgroundImage: `url(${img})`,
                                      backgroundSize: 'cover',
                                      backgroundPosition: 'center',
                                      position: 'relative',
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        background: isExpanded
                                          ? 'linear-gradient(180deg, rgba(0,0,0,0.00) 30%, rgba(0,0,0,0.66) 100%)'
                                          : 'linear-gradient(180deg, rgba(0,0,0,0.00) 35%, rgba(0,0,0,0.68) 100%)',
                                      }}
                                    />
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        left: 12,
                                        right: 12,
                                        top: 10,
                                      }}
                                    >
                                      <Typography
                                        variant="subtitle2"
                                        sx={{
                                          color: 'common.white',
                                          fontWeight: 900,
                                          textShadow: '0 2px 10px rgba(0,0,0,0.45)',
                                          lineHeight: 1.15,
                                          whiteSpace: 'normal',
                                          wordBreak: 'break-word',
                                          display: '-webkit-box',
                                          WebkitLineClamp: isExpanded ? 3 : 2,
                                          WebkitBoxOrient: 'vertical',
                                          overflow: 'hidden',
                                        }}
                                      >
                                        {title}
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        left: 12,
                                        right: 12,
                                        bottom: 10,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-end',
                                        gap: 1,
                                      }}
                                    >
                                      {isAuction && countdown ? (
                                        <Box
                                          sx={{
                                            px: 1,
                                            py: 0.45,
                                            borderRadius: 999,
                                            background: 'linear-gradient(135deg, rgba(239,68,68,0.95) 0%, rgba(249,115,22,0.92) 100%)',
                                            color: 'common.white',
                                            fontWeight: 900,
                                            fontSize: 11,
                                            boxShadow: '0 10px 22px rgba(239,68,68,0.26)',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.6,
                                            border: '1px solid rgba(255,255,255,0.28)',
                                            backdropFilter: 'blur(10px)',
                                            flexShrink: 0,
                                          }}
                                        >
                                          <AccessTimeIcon sx={{ fontSize: 14, color: 'common.white' }} />
                                          {countdown}
                                        </Box>
                                      ) : (
                                        <span />
                                      )}

                                      <Box
                                        sx={{
                                          px: 1,
                                          py: 0.5,
                                          borderRadius: 999,
                                          background: 'rgba(255,255,255,0.92)',
                                          color: 'text.primary',
                                          fontWeight: 900,
                                          fontSize: 12,
                                          boxShadow: '0 10px 22px rgba(0,0,0,0.18)',
                                          flexShrink: 0,
                                        }}
                                      >
                                        {isAuction ? (
                                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.05 }}>
                                            <Typography variant="caption" sx={{ fontWeight: 900, color: 'text.secondary', lineHeight: 1 }}>
                                              Letztes Gebot
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
                                              {new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(displayPriceValue)}
                                            </Typography>
                                          </Box>
                                        ) : (
                                          new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(displayPriceValue)
                                        )}
                                      </Box>
                                    </Box>
                                  </Box>

                                  {isExpanded && (
                                    <Box sx={{ px: 1.25, py: 1.1 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                          <Rating value={rating} precision={0.1} readOnly size="small" />
                                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>
                                            {rating ? rating.toFixed(1) : '0.0'}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                                            ({reviewCount})
                                          </Typography>
                                        </Box>
                                      </Box>

                                      {(categories.length > 0 || tags.length > 0) && (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                                          {categories.slice(0, 2).map((c, idx) => (
                                            <Chip
                                              key={`cat-${idx}`}
                                              label={c}
                                              size="small"
                                              sx={{
                                                height: 22,
                                                fontWeight: 800,
                                                bgcolor: theme => alpha(theme.palette.primary.main, 0.10),
                                                color: theme => theme.palette.primary.dark,
                                                '& .MuiChip-label': { px: 1 },
                                              }}
                                            />
                                          ))}
                                          {tags.slice(0, 3).map((tag, idx) => (
                                            <Chip
                                              key={`tag-${idx}`}
                                              label={tag}
                                              size="small"
                                              variant="outlined"
                                              sx={{
                                                height: 22,
                                                fontWeight: 800,
                                                borderColor: theme => alpha(theme.palette.primary.main, 0.35),
                                                '& .MuiChip-label': { px: 1 },
                                              }}
                                            />
                                          ))}
                                        </Box>
                                      )}
                                    </Box>
                                  )}
                                </Box>
                              );
                            })()}
                          </LeafletTooltip>
                        </Marker>
                      )}

                      {productsWithCoords
                        .map(({ p, lat, lng }) => {
                          const pc: any = transformProductForCard(p);

                          const isAuction = Boolean(pc?.auctionId || pc?.auction?.id || (p as any)?.auction_id);
                          const auctionEnd = String(pc?.auction?.end_time || (pc?.auction as any)?.endTime || (p as any)?.auctions?.end_time || '').trim();
                          const countdown = isAuction ? formatAuctionCountdown(auctionEnd) : '';
                          const auctionBid = pc?.auction?.current_bid;
                          const displayPriceValue = isAuction && typeof auctionBid === 'number' && !isNaN(auctionBid)
                            ? auctionBid
                            : Number(pc?.price ?? p.price ?? 0);

                          const img = pc?.image || (p as any).image || '';
                          const name = pc?.name || p.name;
                          const locationName = String(p.location_name || (p as any).location || '').trim();
                          const categories: string[] = Array.isArray(pc?.categories)
                            ? pc.categories
                            : (pc?.category ? [String(pc.category)] : []);
                          const tags: string[] = Array.isArray(pc?.tags) ? pc.tags : [];
                          const rating = Number(pc?.rating ?? 0);
                          const reviewCount = Number(pc?.reviewCount ?? 0);

                          const isExpanded = expandedMapCardId === String(p.id);

                          return (
                            <Marker key={p.id} position={[lat, lng]} icon={mapMarkerIcon}>
                              <LeafletTooltip
                                className="product-map-tooltip"
                                permanent
                                interactive
                                direction="top"
                                offset={[0, -28]}
                                opacity={1}
                              >
                                <Box
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (!isExpanded) {
                                      setExpandedMapCardId(String(p.id));
                                      return;
                                    }
                                    handleViewDetails(p.id);
                                  }}
                                  sx={{
                                    width: isExpanded ? 280 : 220,
                                    borderRadius: 3,
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    border: theme => `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                                    background: theme => alpha(theme.palette.background.paper, 0.96),
                                    boxShadow: theme => `0 18px 46px ${alpha(theme.palette.common.black, 0.22)}`,
                                    backdropFilter: 'blur(12px)',
                                    '&:hover': {
                                      transform: 'translateY(-1px)',
                                    },
                                    transition: 'all 0.18s ease',
                                    position: 'relative',
                                  }}
                                  role="button"
                                  aria-label="open product"
                                >
                                  {isExpanded && (
                                    <IconButton
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setExpandedMapCardId(null);
                                      }}
                                      sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        zIndex: 2,
                                        width: 30,
                                        height: 30,
                                        bgcolor: theme => alpha(theme.palette.common.white, 0.86),
                                        border: theme => `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                                        '&:hover': { bgcolor: theme => alpha(theme.palette.common.white, 0.94) },
                                      }}
                                      aria-label="close"
                                    >
                                      <CloseIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  )}

                                  <Box
                                    sx={{
                                      height: isExpanded ? 150 : 120,
                                      backgroundImage: `url(${img})`,
                                      backgroundSize: 'cover',
                                      backgroundPosition: 'center',
                                      position: 'relative',
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        background: isExpanded
                                          ? 'linear-gradient(180deg, rgba(0,0,0,0.00) 32%, rgba(0,0,0,0.62) 100%)'
                                          : 'linear-gradient(180deg, rgba(0,0,0,0.00) 35%, rgba(0,0,0,0.68) 100%)',
                                      }}
                                    />
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        left: 12,
                                        right: 12,
                                        top: 10,
                                      }}
                                    >
                                      <Typography
                                        variant="subtitle2"
                                        sx={{
                                          color: 'common.white',
                                          fontWeight: 900,
                                          textShadow: '0 2px 10px rgba(0,0,0,0.45)',
                                          lineHeight: 1.15,
                                          whiteSpace: 'normal',
                                          wordBreak: 'break-word',
                                          display: '-webkit-box',
                                          WebkitLineClamp: isExpanded ? 3 : 2,
                                          WebkitBoxOrient: 'vertical',
                                          overflow: 'hidden',
                                        }}
                                      >
                                        {name}
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        left: 12,
                                        right: 12,
                                        bottom: 10,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-end',
                                        gap: 1,
                                      }}
                                    >
                                      {isAuction && countdown ? (
                                        <Box
                                          sx={{
                                            px: 1,
                                            py: 0.45,
                                            borderRadius: 999,
                                            background: 'linear-gradient(135deg, rgba(239,68,68,0.95) 0%, rgba(249,115,22,0.92) 100%)',
                                            color: 'common.white',
                                            fontWeight: 900,
                                            fontSize: 11,
                                            boxShadow: '0 10px 22px rgba(239,68,68,0.26)',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.6,
                                            border: '1px solid rgba(255,255,255,0.28)',
                                            backdropFilter: 'blur(10px)',
                                            flexShrink: 0,
                                          }}
                                        >
                                          <AccessTimeIcon sx={{ fontSize: 14, color: 'common.white' }} />
                                          {countdown}
                                        </Box>
                                      ) : (
                                        <span />
                                      )}

                                      <Box
                                        sx={{
                                          px: 1,
                                          py: 0.5,
                                          borderRadius: 999,
                                          background: theme => `linear-gradient(135deg, ${alpha(theme.palette.common.white, 0.95)} 0%, ${alpha(theme.palette.common.white, 0.85)} 100%)`,
                                          color: 'text.primary',
                                          fontWeight: 900,
                                          fontSize: 12,
                                          boxShadow: '0 10px 22px rgba(0,0,0,0.18)',
                                          flexShrink: 0,
                                        }}
                                      >
                                        {isAuction ? (
                                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.05 }}>
                                            <Typography variant="caption" sx={{ fontWeight: 900, color: 'text.secondary', lineHeight: 1 }}>
                                              Letztes Gebot
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
                                              {new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(displayPriceValue)}
                                            </Typography>
                                          </Box>
                                        ) : (
                                          new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(displayPriceValue)
                                        )}
                                      </Box>
                                    </Box>
                                  </Box>

                                  {isExpanded && (
                                    <Box sx={{ px: 1.25, py: 1.1 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                          <Rating value={rating} precision={0.1} readOnly size="small" />
                                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>
                                            {rating ? rating.toFixed(1) : '0.0'}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                                            ({reviewCount})
                                          </Typography>
                                        </Box>
                                      </Box>

                                      {(categories.length > 0 || tags.length > 0) && (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                                          {categories.slice(0, 2).map((c, idx) => (
                                            <Chip
                                              key={`cat-${idx}`}
                                              label={c}
                                              size="small"
                                              sx={{
                                                height: 22,
                                                fontWeight: 800,
                                                bgcolor: theme => alpha(theme.palette.primary.main, 0.10),
                                                color: theme => theme.palette.primary.dark,
                                                '& .MuiChip-label': { px: 1 },
                                              }}
                                            />
                                          ))}
                                          {tags.slice(0, 3).map((tag, idx) => (
                                            <Chip
                                              key={`tag-${idx}`}
                                              label={tag}
                                              size="small"
                                              variant="outlined"
                                              sx={{
                                                height: 22,
                                                fontWeight: 800,
                                                borderColor: theme => alpha(theme.palette.primary.main, 0.35),
                                                '& .MuiChip-label': { px: 1 },
                                              }}
                                            />
                                          ))}
                                        </Box>
                                      )}

                                      {!!locationName && (
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, mt: 1, display: 'block' }}>
                                          {locationName}
                                        </Typography>
                                      )}
                                    </Box>
                                  )}
                                </Box>
                              </LeafletTooltip>
                            </Marker>
                          );
                        })
                        .filter(Boolean)}
                    </MapContainer>

                    <Box
                      sx={{
                        position: 'absolute',
                        left: 14,
                        bottom: 14,
                        zIndex: 1100,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: 1,
                        pointerEvents: 'none',
                      }}
                    >
                      <Tooltip title={tx('allProducts.map.style', 'Map style')}>
                        <IconButton
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setMapStyleAnchorEl(e.currentTarget);
                          }}
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 999,
                            border: theme => `1px solid ${alpha(theme.palette.common.white, 0.70)}`,
                            background: theme => `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.92)} 0%, ${alpha(theme.palette.background.paper, 0.78)} 100%)`,
                            boxShadow: theme => `0 10px 26px ${alpha(theme.palette.common.black, 0.22)}`,
                            backdropFilter: 'blur(10px)',
                            pointerEvents: 'auto',
                            '&:hover': {
                              background: theme => `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(theme.palette.background.paper, 0.84)} 100%)`,
                              transform: 'translateY(-1px)',
                              boxShadow: theme => `0 14px 34px ${alpha(theme.palette.common.black, 0.26)}`,
                            },
                            transition: 'all 0.18s ease',
                          }}
                          aria-label="map style"
                        >
                          <LayersIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Menu
                        anchorEl={mapStyleAnchorEl}
                        open={Boolean(mapStyleAnchorEl)}
                        onClose={() => setMapStyleAnchorEl(null)}
                        PaperProps={{
                          sx: {
                            mb: 1,
                            borderRadius: 3,
                            overflow: 'hidden',
                            minWidth: 220,
                            backgroundColor: theme => alpha(theme.palette.background.paper, 0.92),
                            backdropFilter: 'blur(14px)',
                            border: theme => `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                            boxShadow: theme => `0 18px 46px ${alpha(theme.palette.common.black, 0.18)}`,
                          }
                        }}
                        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      >
                        {(['osm', 'hot', 'carto-light', 'carto-dark'] as const).map((id) => {
                          const label = id === 'osm' ? 'OpenStreetMap'
                            : id === 'hot' ? 'OSM Humanitarian'
                            : id === 'carto-light' ? 'Carto Light'
                            : 'Carto Dark';
                          return (
                            <MenuItem
                              key={id}
                              selected={mapStyleId === id}
                              onClick={() => {
                                setMapStyleId(id);
                                setMapStyleAnchorEl(null);
                              }}
                              sx={{
                                py: 1.1,
                                fontWeight: 800,
                              }}
                            >
                              {label}
                            </MenuItem>
                          );
                        })}
                      </Menu>

                      {!isMobile && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pointerEvents: 'none' }}>
                          <Tooltip title={tx('allProducts.map.viewInfo', 'View map info')}>
                            <IconButton
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setMapInfoVisible((v) => !v);
                              }}
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 999,
                                border: theme => `1px solid ${alpha(theme.palette.common.white, 0.70)}`,
                                background: theme => `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.92)} 0%, ${alpha(theme.palette.background.paper, 0.78)} 100%)`,
                                boxShadow: theme => `0 10px 26px ${alpha(theme.palette.common.black, 0.22)}`,
                                backdropFilter: 'blur(10px)',
                                pointerEvents: 'auto',
                                '&:hover': {
                                  background: theme => `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(theme.palette.background.paper, 0.84)} 100%)`,
                                  transform: 'translateY(-1px)',
                                  boxShadow: theme => `0 14px 34px ${alpha(theme.palette.common.black, 0.26)}`,
                                },
                                transition: 'all 0.18s ease',
                              }}
                              aria-label="map info"
                              aria-pressed={mapInfoVisible}
                            >
                              <InfoOutlinedIcon sx={{ fontSize: 22, color: theme => theme.palette.primary.dark }} />
                            </IconButton>
                          </Tooltip>

                          <AnimatePresence initial={false}>
                            {mapInfoVisible && (
                              <motion.div
                                key="map-info-pill"
                                initial={{ opacity: 0, x: -8, scale: 0.98 }}
                                animate={{ opacity: 1, x: 0, scale: 1 }}
                                exit={{ opacity: 0, x: -8, scale: 0.98 }}
                                transition={{ duration: 0.18, ease: 'easeOut' }}
                                style={{ pointerEvents: 'auto' }}
                              >
                                <Box
                                  sx={{
                                    px: 1.2,
                                    py: 0.7,
                                    borderRadius: 999,
                                    border: theme => `1px solid ${alpha(theme.palette.common.white, 0.60)}`,
                                    background: theme => `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.92)} 0%, ${alpha(theme.palette.background.paper, 0.76)} 100%)`,
                                    boxShadow: theme => `0 10px 26px ${alpha(theme.palette.common.black, 0.20)}`,
                                    backdropFilter: 'blur(10px)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 900,
                                      color: 'text.secondary',
                                      letterSpacing: 0.2,
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {tx('allProducts.map.coords', 'Coords')}
                                  </Typography>
                                  <Typography variant="caption" sx={{ fontWeight: 900, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                    {mapViewInfo ? `${mapViewInfo.lat.toFixed(5)}, ${mapViewInfo.lng.toFixed(5)}` : '—'}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 900,
                                      color: 'text.secondary',
                                      letterSpacing: 0.2,
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {tx('allProducts.map.zoom', 'Zoom')}
                                  </Typography>
                                  <Typography variant="caption" sx={{ fontWeight: 900, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                    {mapViewInfo ? String(mapViewInfo.zoom) : '—'}
                                  </Typography>
                                </Box>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Container>

      {/* Filter Sidebar */}
      <FilterSidebar
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        isJobMode={isJobsPage}
        // Current filter states
        searchQuery={searchQuery}
        selectedCategory={selectedCategory}
        sortBy={sortBy}
        priceRange={priceRange}
        purchaseType={purchaseType}
        salaryType={salaryType}
        selectedCondition={selectedCondition}
        showOnlyInStock={showOnlyInStock}
        showOnlyOnSale={showOnlyOnSale}
        excludeOwnProducts={excludeOwnProducts}
        selectedBrands={selectedBrands}
        minRating={minRating}
        selectedSeller={selectedSeller}
        sellerSearchQuery={sellerSearchQuery}
        requiredSkills={requiredSkills}
        selectedTags={selectedTags}
        customFilter1={customFilter1}
        customFilter2={customFilter2}
        customFilter3={customFilter3}
        customFilter4={customFilter4}
        customFilter5={customFilter5}
        // Filter state setters
        setSearchQuery={setSearchQuery}
        setSelectedCategory={setSelectedCategory}
        setSortBy={setSortBy}
        setPriceRange={setPriceRange}
        setPurchaseType={setPurchaseType}
        setSalaryType={setSalaryType}
        setSelectedCondition={setSelectedCondition}
        setShowOnlyInStock={setShowOnlyInStock}
        setShowOnlyOnSale={setShowOnlyOnSale}
        setExcludeOwnProducts={setExcludeOwnProducts}
        setSelectedBrands={setSelectedBrands}
        setMinRating={setMinRating}
        setSelectedSeller={setSelectedSeller}
        setSellerSearchQuery={setSellerSearchQuery}
        setRequiredSkills={setRequiredSkills}
        setSelectedTags={setSelectedTags}
        setCustomFilter1={setCustomFilter1}
        setCustomFilter2={setCustomFilter2}
        setCustomFilter3={setCustomFilter3}
        setCustomFilter4={setCustomFilter4}
        setCustomFilter5={setCustomFilter5}
        // Data
        brands={brands}
        categories={categories}
        loadingCategories={loadingCategories}
        categoriesError={categoriesError}
      />

      {/* Trust Indicators - Only show for shop mode, not job mode */}
      {!isJobsPage && (
        <Box sx={{ bgcolor: 'white', py: 4, mt: 6 }}>
          <Container maxWidth="xl">
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(3, 1fr)'
              },
              gap: 4
            }}>
              <Box sx={{ textAlign: 'center' }}>
                <LocalShipping sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {t('allProducts.features.freeShipping.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('allProducts.features.freeShipping.subtitle')}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Security sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {t('allProducts.features.securePayment.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('allProducts.features.securePayment.subtitle')}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Support sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {t('allProducts.features.support.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('allProducts.features.support.subtitle')}
                </Typography>
              </Box>
            </Box>
          </Container>
        </Box>
      )}

      {/* Sign In Prompt Modal */}
      <SignInPromptModal
        open={signInModalOpen}
        onClose={() => setSignInModalOpen(false)}
        title="Sign In Required"
        message="You need to sign in to save products to your favorites."
      />

      <ShareDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        id={sharePayload?.id}
        url={sharePayload?.url}
        title={sharePayload?.title}
      />

      <Drawer
        anchor="right"
        open={rightSidePanelOpen}
        onClose={() => setJobInfoPanelOpen(false)}
        variant={isMobile ? 'temporary' : 'persistent'}
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: 520, md: 640 },
            maxWidth: '100vw',
            borderLeft: theme => `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.default',
          }
        }}
        ModalProps={{ keepMounted: true }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper',
        }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            {tx('allJobs.preview', 'Job preview')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {/* Job Application Writer */}
            <Tooltip title={jobApplicationWriterMinimized ? tx('header.restoreJobAIWriter', 'Restore Job AI Writer') : tx('header.openJobAIWriter', 'Open Job AI Writer')}>
              <IconButton
                onClick={() => {
                  setJobApplicationWriterMinimized(false);
                  setJobApplicationWriterOpen(true);
                }}
                sx={{
                  borderRadius: 999,
                  width: 40,
                  height: 40,
                  border: '1px solid rgba(0,0,0,0.10)',
                  background: jobApplicationWriterMinimized
                    ? 'linear-gradient(180deg, rgba(16,185,129,0.12) 0%, rgba(16,185,129,0.06) 100%)'
                    : 'linear-gradient(180deg, rgba(139,92,246,0.12) 0%, rgba(139,92,246,0.06) 100%)',
                  color: jobApplicationWriterMinimized ? '#10b981' : '#8b5cf6',
                }}
                aria-label="open job application writer"
              >
                <AutoAwesomeIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Language (same as Header) */}
            <Button
              color="inherit"
              onClick={(e) => setDrawerLangAnchorEl(e.currentTarget)}
              sx={{
                width: 40,
                height: 40,
                minWidth: 'auto',
                borderRadius: 999,
                border: '1px solid rgba(0,0,0,0.10)',
                background: 'linear-gradient(180deg, rgba(2,132,199,0.06) 0%, rgba(2,132,199,0.02) 100%)',
                px: 1.25,
                gap: 0.75,
                '& .MuiButton-startIcon': { m: 0 },
              }}
              aria-label="change language"
            >
              <Box sx={{ lineHeight: 1, display: 'flex', alignItems: 'center' }}>
                {renderFlag(selectedLanguageOption as any, 18, 0)}
              </Box>
              <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.6, lineHeight: 1 }}>
                {String((selectedLanguageOption as any)?.code || language).toUpperCase()}
              </Typography>
            </Button>

            {/* Burger / Nav (open same nav drawer as Header) */}
            <Tooltip title={tx('header.menu', 'Menu')}>
              <IconButton
                onClick={() => {
                  try {
                    window.dispatchEvent(new CustomEvent('open-mobile-menu'));
                  } catch {
                    // ignore
                  }
                }}
                sx={{
                  borderRadius: 999,
                  width: 40,
                  height: 40,
                  border: '1px solid rgba(0,0,0,0.10)',
                  background: 'linear-gradient(180deg, rgba(0,0,0,0.04) 0%, rgba(0,0,0,0.02) 100%)',
                }}
                aria-label="open navigation"
              >
                <MenuIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* User (same as Header) */}
            <Tooltip title={isAuthenticated ? tx('header.profile', 'Profile') : tx('header.login', 'Login')}>
              <IconButton
                onClick={(e) => {
                  if (!isAuthenticated) {
                    navigate('/login');
                    return;
                  }
                  setDrawerUserAnchorEl(e.currentTarget);
                }}
                sx={{
                  borderRadius: 999,
                  width: 40,
                  height: 40,
                  border: '1px solid rgba(0,0,0,0.10)',
                  background: 'linear-gradient(180deg, rgba(2,132,199,0.06) 0%, rgba(2,132,199,0.02) 100%)',
                }}
                aria-label="user menu"
              >
                {isAuthenticated ? (
                  <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main' }}>
                    {String((user as any)?.name || (user as any)?.email || 'U').charAt(0).toUpperCase()}
                  </Avatar>
                ) : (
                  <AccountCircleIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>

            {/* Close */}
            <IconButton onClick={() => setJobInfoPanelOpen(false)} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Drawer header menus (same as Header) */}
        <Menu
          anchorEl={drawerLangAnchorEl}
          open={Boolean(drawerLangAnchorEl)}
          onClose={() => setDrawerLangAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: {
              borderRadius: 3,
              mt: 1,
              minWidth: 280,
              maxWidth: 300,
              boxShadow: '0 10px 30px rgba(2,132,199,0.25)',
              background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.92) 100%)',
              '& .MuiMenuItem-root': {
                gap: 1,
                borderRadius: 2,
                mx: 1,
                my: 0.5,
                px: 1.25,
              },
            },
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              mb: 0.5,
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={autoTranslate}
                  onChange={(e) => {
                    e.stopPropagation();
                    setAutoTranslate(e.target.checked);
                  }}
                  size="small"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: 'primary.main' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'primary.main' },
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TranslateIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                    {t('translation.autoTranslate')}
                  </Typography>
                </Box>
              }
              sx={{
                m: 0,
                '& .MuiFormControlLabel-label': { flex: 1 },
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </Box>

          {languageOptions.map((opt) => {
            const isSupported = Boolean((opt as any).supported);
            const isSelected = language === opt.code;
            return (
              <MenuItem
                key={opt.code}
                onClick={() => handleLanguageSelect(opt.code)}
                selected={isSelected}
                disabled={!isSupported}
                sx={{
                  opacity: isSupported ? 1 : 0.5,
                  cursor: isSupported ? 'pointer' : 'not-allowed',
                  '&:hover': {
                    backgroundColor: isSupported ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                  },
                }}
              >
                {renderFlag(opt as any, 20, 1)}
                <ListItemText
                  primary={opt.label}
                  secondary={isSupported ? opt.code.toUpperCase() : 'Coming soon'}
                  sx={{
                    '& .MuiListItemText-secondary': {
                      color: isSupported ? 'text.secondary' : 'text.disabled',
                      fontSize: '0.75rem',
                    },
                  }}
                />
              </MenuItem>
            );
          })}
        </Menu>

        <Menu
          anchorEl={drawerUserAnchorEl}
          open={Boolean(drawerUserAnchorEl)}
          onClose={() => setDrawerUserAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 280,
              borderRadius: 3,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 18px 60px rgba(2,132,199,0.18)',
              background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.92) 100%)',
              backdropFilter: 'blur(10px)',
              '& .MuiMenuItem-root': {
                borderRadius: 2,
                mx: 1,
                my: 0.5,
                px: 1.25,
                py: 1,
                fontWeight: 700,
                gap: 0.75,
                transition: 'all 0.16s ease',
                '&:hover': {
                  background: 'linear-gradient(180deg, rgba(2,132,199,0.10) 0%, rgba(2,132,199,0.05) 100%)',
                  transform: 'translateY(-1px)',
                },
              },
              '& .MuiListItemIcon-root': {
                minWidth: 34,
                color: 'text.secondary',
              },
              '& .MuiDivider-root': {
                my: 0.75,
                borderColor: 'rgba(148,163,184,0.35)',
              },
            },
          }}
        >
          {isAuthenticated && (
            <Box sx={{ px: 2, pt: 1.75, pb: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontWeight: 900 }}>
                  {String((user as any)?.name || (user as any)?.email || 'U').charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 900, lineHeight: 1.1 }} noWrap>
                    {(user as any)?.name || tx('header.profile', 'Profile')}
                  </Typography>
                  {drawerUserEmail ? (
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }} noWrap>
                      {drawerUserEmail}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
              <Divider sx={{ mt: 1.25, mb: 0.25 }} />
            </Box>
          )}

          {userMenuItems.map((item) => (
            <MenuItem
              key={item.path}
              onClick={() => {
                navigate(item.path);
                setDrawerUserAnchorEl(null);
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </MenuItem>
          ))}
          <MenuItem
            onClick={() => {
              try {
                window.dispatchEvent(new CustomEvent('open-messages-sidebar'));
              } catch {
                // ignore
              }
              setDrawerUserAnchorEl(null);
            }}
          >
            <ListItemIcon>
              <MessageIcon />
            </ListItemIcon>
            <ListItemText primary={t('header.messages')} />
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={() => {
              try {
                supabase.auth.signOut();
              } catch {
                // ignore
              }
              try {
                logout();
              } catch {
                // ignore
              }
              setDrawerUserAnchorEl(null);
              navigate('/');
            }}
          >
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary={t('header.logout')} />
          </MenuItem>
        </Menu>

        <Box sx={{ flex: 1, height: '100%', overflow: 'auto' }}>
          {defaultJobDetailId ? (
            <Routes
              location={{
                pathname: `${location.pathname.replace(/\/$/, '')}/product/${defaultJobDetailId}`,
                search: '',
                hash: '',
                state: null,
                key: 'job-info',
              }}
            >
              <Route path="product/:id" element={<ProductDetailPage />} />
            </Routes>
          ) : (
            <Box sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary">
                No job selected
              </Typography>
            </Box>
          )}
        </Box>
      </Drawer>

      <ProductReelsViewer
        open={reelsOpen}
        onClose={() => setReelsOpen(false)}
        items={paginatedProducts}
        title={isJobsPage ? (t('allProducts.pagination.jobsFound') || '') : (t('allProducts.pagination.productsFound') || '')}
        renderItem={(product) => (
          <ProductCard
            product={transformProductForCard(product)}
            onAddToCart={handleAddToCart}
            onToggleFavorite={handleToggleFavorite}
            onViewDetails={handleViewDetails}
            onShare={handleShare}
            isFavorite={favorites.has(product.id)}
            isOwnProduct={isAuthenticated && user?.id === product.sellerId}
            showSeller={false}
            variant="default"
            navigationState={{
              currentPage,
              filters: {
                searchQuery,
                selectedCategory,
                sortBy,
                priceRange,
                selectedCondition,
                showOnlyInStock,
                selectedBrands: Array.from(selectedBrands),
                minRating,
                viewMode,
              },
              searchQuery,
              sortBy,
              viewMode,
            }}
          />
        )}
      />

    </Box>
  );
};

export default AllProductsPage;
